import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ShiftRosterService, isAdministrativeRole } from '../../services/shiftRoster';
import { LeaveService } from '../../services/leave';
import { LeaveRequest, LeaveStatus } from '../../types/leave';
import {
  ShiftMaster,
  EmployeeShiftRow,
  CreateShiftAssignmentInput,
  BulkOverrideEmployee,
  BatchShiftResult,
} from '../../types/shiftRoster';
import {
  canManageShiftMaster,
  canAccessShiftRoster,
  canAccessShiftReports,
  canEditEmployeeRoster,
} from '../../utils/rolePermissions';
import ShiftReportsTab from './ShiftReportsTab';
import {
  findConfusableShift,
  holidayTone,
  isRosterLeaveType,
  leaveChipDisplay,
  PUBLIC_HOLIDAY_CODE,
  resolveConfiguredShift,
  resolveLeaveCode,
  shiftCellDisplay,
  toneFrom,
} from './shiftRosterDisplay';
import { OrgConfigService } from '../../services/orgConfigService';
import { useTheme } from '../../contexts/ThemeContext';
import { loadAllActiveEmployees, compareByEmployeeId } from '../../services/userLoader';
import { hasLeftOrganisation } from '../../services/employmentStatus';
import { CompOffService } from '../../services/compOffService';
import { sameIdentity } from '../../services/identity';
import { getHolidayForDate } from '../../data/holidays';
import RightSidePanel from '../performance/RightSidePanel';
import EmptyState from '../common/EmptyState';
import './ShiftRosterPage.css';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

/**
 * Rosters are only ever planned forward, so the picker offers the current year and the
 * next two — nothing historical and nothing speculatively far out. Computed at render
 * rather than hardcoded: the old fixed [2024…2028] list silently went stale every year
 * and already exposed two years nobody could roster into.
 */
const rosterYears = (): number[] => {
  const current = new Date().getFullYear();
  return [current, current + 1, current + 2];
};

/**
 * The two shifts the roster grid draws as colour only, with no code printed in the cell.
 *
 * Both are the unremarkable case, and between them they account for nearly every cell in a
 * normal month: GEN on the working days, OFF on the weekends. Printing a label on all of them
 * spends the grid's ink on what a reader already assumes and leaves the exceptions it exists
 * to surface — a night shift, a leave, a manual override, a retired code — competing for
 * attention with the norm. The cell still carries the shift's configured colour, so an OFF day
 * stays distinguishable from a day with no shift at all, the Shift Types legend below the grid
 * names every colour, and the cell's tooltip always spells the code out in full.
 *
 * Named constants rather than inline comparisons so the rule is findable, and matched
 * case-insensitively because the codes are free text entered in Shift Configuration.
 */
const DEFAULT_SHIFT_CODE = 'GEN';
const WEEKLY_OFF_SHIFT_CODE = 'OFF';

const isUnlabelledShiftCode = (code?: string): boolean => {
  const normalized = (code || '').trim().toUpperCase();
  return normalized === DEFAULT_SHIFT_CODE || normalized === WEEKLY_OFF_SHIFT_CODE;
};

/**
 * Leave types the legend explains, named the way records name them.
 *
 * Names rather than codes and colours, because both of those are now resolved through
 * leaveChipDisplay against the configured master list — the legend describes what the grid
 * draws instead of asserting its own palette. Public Holiday is included here so it stops
 * being a one-off hardcoded swatch.
 */
const LEAVE_LEGEND_TYPES = [
  'Public Holiday',
  'Casual Leave',
  'Sick Leave',
  'Earned Leave',
  'Comp-Off',
  // Displayed as "Unpaid Leave (UL)". "LOP" is still recognised as an input value — see
  // UNPAID_LEAVE_CODE — but is no longer shown anywhere.
  'Unpaid Leave',
  // Work From Home and On Duty are deliberately absent: they are withdrawn from the roster
  // (WITHDRAWN_LEAVE_CODES), so a legend entry would explain a chip that is never drawn.
];

/**
 * Sentinel for the "no team set" filter option.
 *
 * A distinct value rather than '' because '' already means "All Departments", and a real team
 * can never collide with it.
 */
const UNASSIGNED_TEAM = '__UNASSIGNED__';


type ActiveTab = 'details' | 'config' | 'bulk' | 'reports';

// Skeleton table loader during User Management fetch
const SkeletonRosterTable: React.FC = () => (
  <div className="sr-skeleton-wrapper">
    <div className="sr-skeleton-header">
      <div className="sr-skeleton-bar short" />
      <div className="sr-skeleton-bar long" />
    </div>
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="sr-skeleton-row">
        <div className="sr-skeleton-cell sr-skeleton-emp" />
        <div className="sr-skeleton-cell sr-skeleton-dept" />
        <div className="sr-skeleton-cell sr-skeleton-reg" />
        {Array.from({ length: 15 }).map((_, j) => (
          <div key={j} className="sr-skeleton-cell sr-skeleton-day" />
        ))}
      </div>
    ))}
  </div>
);

// Skeleton table loader for Shift Configuration while listShiftMasters() is in flight
const SkeletonShiftConfigTable: React.FC = () => (
  <div className="sr-skeleton-wrapper">
    <div className="sr-skeleton-header">
      <div className="sr-skeleton-bar short" />
      <div className="sr-skeleton-bar long" />
    </div>
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="sr-skeleton-row">
        <div className="sr-skeleton-cell sr-skeleton-emp" />
        <div className="sr-skeleton-cell sr-skeleton-dept" />
        <div className="sr-skeleton-cell sr-skeleton-reg" />
        <div className="sr-skeleton-cell sr-skeleton-dept" />
        <div className="sr-skeleton-cell sr-skeleton-reg" />
      </div>
    ))}
  </div>
);

// ── Shared: Confirmation Dialog (Delete/Deactivate in Shift Config, bulk-apply in Bulk Override) ──
interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string | null;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open, title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', danger, busy, onConfirm, onCancel,
}) => {
  if (!open) return null;
  return (
    <div className="sr-confirm-overlay" onClick={onCancel}>
      <div className="sr-confirm-card" role="alertdialog" aria-modal="true" aria-label={title} onClick={e => e.stopPropagation()}>
        <h3 className="sr-confirm-title">{title}</h3>
        <div className="sr-confirm-message">{message}</div>
        <div className="sr-confirm-actions">
          {cancelLabel && (
            <button type="button" className="sr-btn-cancel" onClick={onCancel} disabled={busy}>{cancelLabel}</button>
          )}
          <button type="button" className={`sr-btn-save ${danger ? 'sr-btn-danger' : ''}`} onClick={onConfirm} disabled={busy}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Shared: Weekend Pattern helpers (ShiftMaster.weekendDays, 0=Sun..6=Sat, default [0,6]) ──
const getWeekendDays = (s: ShiftMaster): number[] => (s.weekendDays && s.weekendDays.length > 0 ? s.weekendDays : [0, 6]);
const weekendPatternKey = (s: ShiftMaster): string => getWeekendDays(s).slice().sort((a, b) => a - b).join(',');
const weekendPatternLabel = (s: ShiftMaster): string => getWeekendDays(s).slice().sort((a, b) => a - b).map(d => WEEKDAYS[d]).join('/');

// Mirrors the backend's deriveNightShift() so the form can suggest a live default —
// isNightShift remains an explicit, independently-editable stored field either way.
const deriveNightShiftClient = (startTime: string, endTime: string): boolean => {
  if (!startTime || !endTime) return false;
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  if ([sh, sm, eh, em].some(n => Number.isNaN(n))) return false;
  return (eh * 60 + em) <= (sh * 60 + sm);
};

/**
 * Whether a shift counts as a night shift for filtering and display.
 *
 * Prefers the stored flag, falling back to deriving it from the times when the field is
 * absent — records written before isNightShift was persisted have it undefined, and
 * treating those as "not a night shift" is what made the Night filter return nothing and
 * the Day filter return everything.
 */
const isNightShift = (s: ShiftMaster): boolean =>
  typeof s.isNightShift === 'boolean' ? s.isNightShift : deriveNightShiftClient(s.startTime, s.endTime);

/** Moon marker shown wherever a night shift is named, so the grid, table and every
 *  dropdown agree at a glance. */
const NightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <span className={className ? `sr-moon ${className}` : 'sr-moon'} role="img" aria-label="Night shift" title="Night shift">🌙</span>
);

/** Same marker for plain-text contexts (<option> labels can't hold elements). */
const nightPrefix = (s: ShiftMaster): string => (isNightShift(s) ? '🌙 ' : '');

const formatDateShort = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ════════════════════════════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════════════════════════════

const ShiftRosterPage: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [shiftMaster, setShiftMaster] = useState<ShiftMaster[]>([]);
  const [shiftMasterLoading, setShiftMasterLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Drive active tab strictly from sub-route URL parameter
  let activeTab: ActiveTab = 'details';
  if (location.pathname.endsWith('/shift-configuration')) activeTab = 'config';
  if (location.pathname.endsWith('/bulk-override')) activeTab = 'bulk';
  if (location.pathname.endsWith('/reports')) activeTab = 'reports';

  const canManage = user?.role ? canAccessShiftRoster(user.role) : false;
  const canConfig  = user?.role ? canManageShiftMaster(user.role)  : false;
  const canReport  = user?.role ? canAccessShiftReports(user.role) : false;

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadShiftMaster = useCallback(async () => {
    setShiftMasterLoading(true);
    try {
      const data = await ShiftRosterService.listShiftMasters();
      setShiftMaster(data);
    } catch (err) {
      console.error('[ShiftRoster] Load shifts error:', err);
    } finally {
      setShiftMasterLoading(false);
    }
  }, []);

  useEffect(() => { loadShiftMaster(); }, [loadShiftMaster]);

  const activeShifts = useMemo(() => shiftMaster.filter(s => s.isActive), [shiftMaster]);

  const getSubmenuTitle = () => {
    switch (activeTab) {
      case 'config': return 'Shift Configuration';
      case 'bulk': return 'Bulk Shift Override';
      case 'reports': return 'Shift Reports';
      default: return 'Shift Details';
    }
  };

  return (
    <section className="shift-roster-page">
      {toast && <div className={`sr-toast sr-toast--${toast.type}`}>{toast.type === 'success' ? '✓' : '✗'} {toast.message}</div>}

      <div className="sr-header">
        <div>
          <h1>{getSubmenuTitle()}</h1>
        </div>
      </div>

      {activeTab === 'details' && <ShiftDetailsTab shifts={shiftMaster} canManage={canManage} showToast={showToast} />}
      {activeTab === 'config' && canConfig && (
        <ShiftConfigTab shifts={shiftMaster} loading={shiftMasterLoading} onReload={loadShiftMaster} showToast={showToast} />
      )}
      {activeTab === 'bulk' && (
        <BulkOverrideTab shifts={activeShifts} allShifts={shiftMaster} canManage={canManage} showToast={showToast} />
      )}
      {/* Gated like the config tab: the route guard says the same thing, and without this a
          direct URL for a role that slipped past would render a heading over an empty body. */}
      {activeTab === 'reports' && canReport && (
        <ShiftReportsTab shifts={shiftMaster} showToast={showToast} />
      )}
    </section>
  );
};

interface ShiftCellDropdownPortalProps {
  anchorEl: HTMLElement;
  activeShifts: ShiftMaster[];
  currentShiftCode: string;
  isOverride: boolean;
  onSelectShift: (shiftCode: string) => void;
  onClearOverride: () => void;
  onClose: () => void;
}

const ShiftCellDropdownPortal: React.FC<ShiftCellDropdownPortalProps> = ({
  anchorEl,
  activeShifts,
  currentShiftCode,
  isOverride,
  onSelectShift,
  onClearOverride,
  onClose,
}) => {
  const [style, setStyle] = useState<React.CSSProperties>({ opacity: 0, pointerEvents: 'none' });
  const dropdownRef = useRef<HTMLDivElement>(null);

  const calculatePosition = useCallback(() => {
    if (!anchorEl || !dropdownRef.current) return;
    const rect = anchorEl.getBoundingClientRect();
    const dropdownEl = dropdownRef.current;

    const dropdownWidth = Math.max(160, dropdownEl.offsetWidth || 180);
    const dropdownHeight = dropdownEl.offsetHeight || 220;

    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Rule:
    // If space below is insufficient for full height but space above is larger, open upward.
    const openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    const top = openUpward
      ? Math.max(8, rect.top - dropdownHeight - 6)
      : Math.min(viewportHeight - dropdownHeight - 8, rect.bottom + 6);

    // Center horizontally over the cell, but clamp inside viewport bounds
    let left = rect.left + rect.width / 2 - dropdownWidth / 2;
    left = Math.max(12, Math.min(left, viewportWidth - dropdownWidth - 12));

    const maxHeight = openUpward
      ? Math.min(340, rect.top - 16)
      : Math.min(340, viewportHeight - rect.bottom - 16);

    setStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      minWidth: `${dropdownWidth}px`,
      maxHeight: `${maxHeight}px`,
      overflowY: 'auto',
      zIndex: 9999,
      opacity: 1,
      pointerEvents: 'auto',
    });
  }, [anchorEl]);

  useEffect(() => {
    const timer = requestAnimationFrame(() => calculatePosition());

    const handleScrollOrResize = () => {
      calculatePosition();
    };

    window.addEventListener('resize', handleScrollOrResize);
    window.addEventListener('scroll', handleScrollOrResize, true);

    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        !anchorEl.contains(target)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      cancelAnimationFrame(timer);
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [anchorEl, calculatePosition, onClose]);

  return ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className="sr-cell-dropdown sr-cell-dropdown-portal"
      style={style}
    >
      {activeShifts.map(s => (
        <button
          key={s.shiftCode}
          type="button"
          className={`sr-dd-item ${s.shiftCode === currentShiftCode ? 'selected' : ''}`}
          onClick={e => {
            e.stopPropagation();
            onSelectShift(s.shiftCode);
          }}
        >
          <span className="sr-dd-dot" style={{ background: s.color }} />
          <span>{s.shiftCode}{isNightShift(s) && <NightIcon />}</span>
          <small className="sr-dd-time">{s.startTime}</small>
        </button>
      ))}
      {isOverride && (
        <button
          type="button"
          className="sr-dd-item"
          style={{ color: '#ef4444', borderTop: '1px solid #e2e8f0', marginTop: 4, paddingTop: 6 }}
          onClick={e => {
            e.stopPropagation();
            onClearOverride();
          }}
        >
          <span>↩ Clear override</span>
        </button>
      )}
    </div>,
    document.body
  );
};

/**
 * The row action menu on Shift Configuration, anchored to its ⋮ button but rendered in a
 * portal.
 *
 * It used to be an absolutely-positioned child of the cell, which put it inside
 * .sr-shifts-table-wrapper — a container with `overflow:auto` and a capped max-height. On the
 * lower rows the menu extended past that container's bottom edge and was clipped, so the
 * options appeared to "go down" out of view and could only be reached by scrolling the table,
 * which scrolled the row itself away. Flipping the menu upward does not fix it either: with
 * only a handful of shifts configured the wrapper is barely taller than the menu, so an
 * upward menu clips against the header instead. Escaping the container is the only position
 * that works at every table height, which is what ShiftCellDropdownPortal already does for
 * the roster grid's cell dropdown.
 *
 * Right-aligned to the button rather than centred on it, matching where the menu sat before,
 * and clamped inside the viewport on both axes.
 */
const RowMenuPortal: React.FC<{
  anchorEl: HTMLElement;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ anchorEl, onClose, children }) => {
  const [style, setStyle] = useState<React.CSSProperties>({ opacity: 0, pointerEvents: 'none' });
  const menuRef = useRef<HTMLDivElement>(null);

  const calculatePosition = useCallback(() => {
    if (!anchorEl || !menuRef.current) return;
    const rect = anchorEl.getBoundingClientRect();
    const el = menuRef.current;
    const width = Math.max(150, el.offsetWidth || 150);
    const height = el.offsetHeight || 160;
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    const spaceBelow = vh - rect.bottom;
    const openUpward = spaceBelow < height && rect.top > spaceBelow;
    const top = openUpward
      ? Math.max(8, rect.top - height - 6)
      : Math.min(vh - height - 8, rect.bottom + 6);

    // Right edge of the menu aligns with the right edge of the button.
    const left = Math.max(12, Math.min(rect.right - width, vw - width - 12));

    setStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      minWidth: `${width}px`,
      zIndex: 9999,
      opacity: 1,
      pointerEvents: 'auto',
    });
  }, [anchorEl]);

  useEffect(() => {
    const timer = requestAnimationFrame(() => calculatePosition());
    const handleScrollOrResize = () => calculatePosition();
    window.addEventListener('resize', handleScrollOrResize);
    // Capture phase: the table wrapper scrolls, not the window, and a scroll event on it
    // does not bubble. Without this the menu would hang in place while its row moved.
    window.addEventListener('scroll', handleScrollOrResize, true);

    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current && !menuRef.current.contains(target) && !anchorEl.contains(target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      cancelAnimationFrame(timer);
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [anchorEl, calculatePosition, onClose]);

  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      className="sr-row-menu sr-row-menu-portal"
      style={style}
      role="menu"
    >
      {children}
    </div>,
    document.body
  );
};

// ════════════════════════════════════════════════════════════════
// TAB 1: SHIFT DETAILS (Roster Grid)
// Single Source of Truth: Primary Shift from employee record
// fills every working day automatically. Leave overlays on top.
// ════════════════════════════════════════════════════════════════

interface ShiftDetailsTabProps {
  shifts: ShiftMaster[];
  canManage: boolean;
  showToast: (msg: string, type: 'success' | 'error') => void;
}

const ShiftDetailsTab: React.FC<ShiftDetailsTabProps> = ({ shifts, canManage, showToast }) => {
  const { user } = useAuth();
  // Tones are computed in JS from configured seeds, so the theme has to be readable here — a
  // [data-theme] CSS override would be a second source of colour, which is the thing being fixed.
  const { isDarkMode } = useTheme();
  const [month, setMonth]           = useState(new Date().getMonth());
  const [year, setYear]             = useState(new Date().getFullYear());
  // Recomputed per mount rather than module-load so a long-lived tab open across New Year
  // still offers the right window.
  const selectableYears = useMemo(() => rosterYears(), []);
  const [search, setSearch]         = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [roster, setRoster]         = useState<EmployeeShiftRow[]>([]);
  const [leaves, setLeaves]         = useState<LeaveRequest[]>([]);
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState<string | null>(null);
  const [activeCell, setActiveCell] = useState<{ empId: string; date: string; el: HTMLElement } | null>(null);
  const [savingCellKey, setSavingCellKey] = useState<string | null>(null);

  // Range-override panel state (apply a shift across a From–To range for one/all visible employees)
  const [rangeOpen, setRangeOpen]   = useState(false);
  const [rangeEmp, setRangeEmp]     = useState<string>('ALL');
  const [rangeFrom, setRangeFrom]   = useState('');
  const [rangeTo, setRangeTo]       = useState('');
  const [rangeShift, setRangeShift] = useState('');
  const [rangeBusy, setRangeBusy]   = useState(false);

  // ── Data loading ─────────────────────────────────────────────
  const loadRoster = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [data, leaveData] = await Promise.all([
        ShiftRosterService.getRoster(month, year, user?.email, user?.role),
        LeaveService.listLeaves(),
      ]);
      setRoster(data);
      const approved = (leaveData || []).filter(
        (l: any) => String(l.status || '').toUpperCase() === 'APPROVED' || l.status === LeaveStatus.APPROVED
      );
      setLeaves(approved);
    } catch (err) {
      console.error('[ShiftRoster] Roster load error:', err);
      setRoster([]);
      setLoadError('Unable to load employees');
    } finally {
      setLoading(false);
    }
  }, [month, year, user?.email, user?.role]);

  useEffect(() => { loadRoster(); }, [loadRoster]);

  // ── Derived data ─────────────────────────────────────────────
  const days = useMemo(() => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => {
      const d = i + 1;
      const dateObj = new Date(year, month, d);
      return {
        date: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
        day: d,
        weekday: WEEKDAYS[dateObj.getDay()],
      };
    });
  }, [month, year]);

  /*
   * The Team / Department list HR maintains in Organisation Setup — the same
   * OrgConfigService.getTeams() that populates User Management's dropdown.
   *
   * Previously this was the distinct set of values found on the loaded rows, which had two
   * consequences: the options depended on whichever employees happened to be in view (so they
   * changed with the month, the search box and the manager's own team), and it listed the
   * free-text Job Title field, typos included. Reading the maintained list instead means an
   * option exists here exactly when it exists in User Management.
   *
   * Held in state with the same 'org-config-updated' listener User Management uses, so HR
   * editing the list in the next tab does not require a reload.
   */
  const [teamOptions, setTeamOptions] = useState<string[]>(() => OrgConfigService.getTeams());

  useEffect(() => {
    const readTeams = () => setTeamOptions(OrgConfigService.getTeams());
    OrgConfigService.refresh().finally(readTeams);
    window.addEventListener('org-config-updated', readTeams);
    return () => window.removeEventListener('org-config-updated', readTeams);
  }, []);

  /** Employees whose team is unset — a real state, surfaced rather than hidden. */
  const unassignedCount = useMemo(
    () => roster.filter(r => !r.team).length,
    [roster]
  );

  const filteredRoster = useMemo(() => roster.filter(r => {
    const q = search.trim().toLowerCase();
    /*
     * Searchable on the things a person can actually type: name, HR employee number, email.
     * This used to match on employeeId — the Cognito account id — so searching an employee
     * number found nobody, and the only string that did match was one nobody has to hand.
     */
    const matchesSearch = !q ||
      r.employeeName.toLowerCase().includes(q) ||
      (r.employeeCode || '').toLowerCase().includes(q) ||
      (r.employeeEmail || '').toLowerCase().includes(q);
    /*
     * Matched against `team`, the field backed by HR's maintained list, not against the
     * free-text Job Title the column used to filter on. UNASSIGNED_TEAM is the explicit
     * "nobody has set this" bucket, so those employees remain reachable instead of being
     * findable only under All Departments.
     */
    const matchesTeam =
      !deptFilter ||
      (deptFilter === UNASSIGNED_TEAM ? !r.team : r.team === deptFilter);
    return matchesSearch && matchesTeam && !isAdministrativeRole(r.role);
  })
    /*
     * Ordered by employee number, not by whatever order the directory returned.
     *
     * Cognito's listUsers has no meaningful order, so the roster's row order shifted between
     * loads and there was no way to find a person except by searching. Sorted numerically —
     * `localeCompare` with `numeric` so CRT0044 sits before CRT0100 rather than after it, which a
     * plain string sort gets wrong for any unpadded number.
     *
     * Employees with no number sort last, by name: they are a data gap, and burying them at the
     * top under an empty string would put them where the lowest IDs belong.
     */
    .slice()
    // The shared comparator, so this grid and User Management cannot order people differently.
    // EmployeeShiftRow names the fields differently, hence the adapter.
    .sort((a, b) => compareByEmployeeId(
      { employeeId: a.employeeCode, name: a.employeeName },
      { employeeId: b.employeeCode, name: b.employeeName }
    )),
  [roster, search, deptFilter]);

  // ── Leave helpers ─────────────────────────────────────────────
  /**
   * The leave that actually books this employee off on this date, if any.
   *
   * Two things were wrong here, and together they are why the roster showed some leaves and
   * not others:
   *
   *  1. The email was compared with `!==` on lowercased strings. People in this directory own
   *     two Cognito accounts on two domains (creyentetech.com and creyenteinfotech.com) —
   *     loadAllUsers merges them into one row and the roster already queries assignments across
   *     both (see getAssignmentsForAccounts). Leave, though, is stored against whichever address
   *     was current when the request was raised, so any leave filed under the employee's *other*
   *     address silently failed to match the merged row and simply never appeared on the grid.
   *     sameIdentity is the domain-aware comparison the rest of the app uses for exactly this.
   *
   *  2. Status was never checked, despite the name. A Rejected or Cancelled request matched just
   *     as readily as an approved one, so a refused leave request still painted the cell as
   *     leave — and because the cell then short-circuits before the override/holiday/primary
   *     shift branches, it also hid the shift the employee was in fact working. Fixing this
   *     will remove some cells that should never have been there.
   *
   * Pending is deliberately excluded: the roster states what is booked, and a request nobody
   * has approved yet books nothing.
   */
  const getApprovedLeave = useCallback((email: string, date: string): LeaveRequest | undefined => {
    if (!email) return undefined;
    return leaves.find(l => {
      if (!sameIdentity(l.employeeEmail, email)) return false;
      const status = String(l.status || '').toUpperCase();
      if (status !== String(LeaveStatus.APPROVED).toUpperCase()) return false;
      return date >= l.startDate && date <= l.endDate;
    });
  }, [leaves]);

  /*
   * Leave code and colour both come from shiftRosterDisplay, which resolves them against the
   * configured leave master list — the same source the Leave module reads. The two used to be
   * derived independently here: the code by substring guessing (so "Comp-Off" printed as COMP
   * against CO in Leave Policies) and the colour from a hardcoded table where Casual, Sick and
   * Earned were all the same amber. Same record, different answer on each screen.
   */
  const getLeaveCode = (type: string): string => resolveLeaveCode(type);

  // Only active shifts for the picker dropdown and the legend.
  const activeShifts = useMemo(() => shifts.filter(s => s.isActive), [shifts]);

  /*
   * The codes Shift Configuration currently offers.
   *
   * A cell renders whatever string the employee record carries, so a shift deleted or
   * deactivated after it was assigned keeps displaying as though it were real — with no
   * hours, no colour and no legend entry beside it. Codes have also been reused over time
   * (EMS meant Evening Shift before it meant Early Morning Shift), which makes a retired
   * code actively misleading rather than merely unstyled. Anything outside this set is
   * drawn as stale instead of as a normal shift.
   */
  /*
   * Staleness is decided by the same resolver that decides the cell's colour.
   *
   * It used to be an independent Set of upper-cased shift codes, which is how a cell could be
   * painted by one rule and judged by another. Now that resolveConfiguredShift also matches a
   * stored shift *name* — see its docstring for why that is a data-model fact rather than a
   * special case — a name-matched cell would have been coloured correctly and simultaneously
   * flagged "not an active shift". One function answers both questions.
   */
  const isStaleCode = useCallback(
    (code: string): boolean =>
      // OFF is never a ShiftMaster record — it is the roster's own weekly-off marker — so it
      // is known by definition and must not be flagged.
      !!code && code.toUpperCase() !== 'OFF' && !resolveConfiguredShift(code, activeShifts),
    [activeShifts]
  );

  // ── Priority Engine ──────────────────────────────────────────
  // Sequence:
  // 1. Approved Leave (Highest - read-only)
  // 2. Manual Shift Override (Overrides Holiday & Weekend)
  // 3. Holiday (Defaults to OFF/HOL, editable)
  // 4. Weekly Off (Sat/Sun, defaults to OFF, editable)
  // 5. Primary Shift (Default weekday schedule)

  interface CellState {
    shiftCode: string;
    isOverride: boolean;
    isHoliday: boolean;
    holidayName?: string;
    isWeekend: boolean;
    isLeave: boolean;
    leaveCode?: string;
    leaveRequest?: LeaveRequest;
    /** Code no longer defined in Shift Configuration — see isStaleCode. */
    isStale?: boolean;
    /**
     * Whether this day actually differs from what the employee would have worked anyway.
     *
     * Separate from `isOverride`, which only says a stored assignment exists. Setting a cell back
     * to the shift it would have had — a manager undoing a change by re-picking the original —
     * still writes an assignment, so `isOverride` stays true and the amber "modified" ring stayed
     * on a day that is no longer modified in any sense a reader cares about. The ring now follows
     * this instead, so it marks a real deviation rather than the mere existence of a record.
     *
     * Clearing an override removes the record outright, which already dropped the ring; this is
     * the other half of "changed back to normal".
     */
    differsFromDefault?: boolean;
    /**
     * An approved Work From Home / On Duty record covering this date.
     *
     * Those types no longer take over the cell — the employee is working, so the roster shows
     * the shift they are working — but the record still exists and is named in the tooltip
     * rather than silently dropped.
     */
    withdrawnLeaveNote?: string;
  }

  const getCellState = useCallback((emp: EmployeeShiftRow, d: { date: string; weekday: string }): CellState => {
    const approvedLeave = getApprovedLeave(emp.employeeEmail, d.date);
    // WFH and On Duty are withdrawn from the roster (isRosterLeaveType); they describe someone
    // working, not someone absent, so they annotate the cell instead of replacing its shift.
    const leaveOwnsCell = !!approvedLeave && isRosterLeaveType(approvedLeave.type);
    const withdrawnLeaveNote = approvedLeave && !leaveOwnsCell
      ? `${approvedLeave.type} recorded for this date`
      : undefined;

    if (approvedLeave && leaveOwnsCell) {
      const leaveCode = getLeaveCode(approvedLeave.type);
      return {
        shiftCode: leaveCode,
        isOverride: false,
        isHoliday: false,
        isWeekend: d.weekday === 'Sat' || d.weekday === 'Sun',
        isLeave: true,
        leaveCode,
        leaveRequest: approvedLeave,
      };
    }

    const override = emp.shifts?.[d.date];
    const holiday = getHolidayForDate(d.date);
    const isWeekend = d.weekday === 'Sat' || d.weekday === 'Sun';

    /*
     * What this day would be with no stored assignment at all — the rest of the chain below,
     * evaluated up front so the override branch can tell whether it actually changes anything.
     */
    const defaultCode = holiday || isWeekend
      ? WEEKLY_OFF_SHIFT_CODE
      : (emp.primaryShift || emp.shift || '');

    // 1. Manual Shift Override (Overrides Holiday & Weekend)
    if (override !== undefined && override !== '') {
      return {
        shiftCode: override,
        isOverride: true,
        isHoliday: !!holiday,
        holidayName: holiday?.name,
        isWeekend,
        isLeave: false,
        withdrawnLeaveNote,
        // A saved override outlives the shift it names, exactly like a primary shift does.
        isStale: isStaleCode(override),
        // Case-insensitive: the stored code and the primary shift are both free text.
        differsFromDefault: override.trim().toUpperCase() !== defaultCode.trim().toUpperCase(),
      };
    }

    // 2. Holiday (if no manual override)
    if (holiday) {
      return {
        shiftCode: 'OFF',
        isOverride: false,
        isHoliday: true,
        holidayName: holiday.name,
        isWeekend,
        isLeave: false,
        withdrawnLeaveNote,
      };
    }

    // 3. Weekly Off (if no manual override)
    if (isWeekend) {
      return {
        shiftCode: 'OFF',
        isOverride: false,
        isHoliday: false,
        isWeekend: true,
        isLeave: false,
        withdrawnLeaveNote,
      };
    }

    // 5. Employee Primary Shift (default working-day schedule directly from User Management)
    const userPrimaryShift = emp.primaryShift || emp.shift || '';
    return {
      shiftCode: userPrimaryShift,
      isOverride: false,
      isHoliday: false,
      isWeekend: false,
      isLeave: false,
      isStale: isStaleCode(userPrimaryShift),
      withdrawnLeaveNote,
    };
    // `leaves` is not a dependency: getApprovedLeave is itself memoised on it, so a change to
    // leaves produces a new getApprovedLeave and re-triggers this. Listing it as well was a
    // redundancy the build flagged, not a second source of freshness.
  }, [getApprovedLeave, isStaleCode]);

  // REG = days with a shift that is not OFF and not on approved leave
  const getRegCount = (emp: EmployeeShiftRow): number => {
    return days.filter(d => {
      const state = getCellState(emp, d);
      if (state.isLeave) return false;
      return state.shiftCode && state.shiftCode.toUpperCase() !== 'OFF';
    }).length;
  };

  // OFF count for summary
  const getOffCount = (emp: EmployeeShiftRow): number => {
    return days.filter(d => {
      const state = getCellState(emp, d);
      if (state.isLeave) return false;
      return !state.shiftCode || state.shiftCode.toUpperCase() === 'OFF';
    }).length;
  };

  /*
   * Retired codes actually in use this month.
   *
   * The legend is built from active shifts only, so without this a stale chip in the grid
   * has nothing beside it explaining what it is — which is the state that let one employee
   * sit on EMS unnoticed while no EMS record existed.
   */
  const staleCodesInUse = useMemo(() => {
    const found = new Set<string>();
    filteredRoster.forEach(emp => days.forEach(d => {
      const state = getCellState(emp, d);
      if (state.isStale) found.add(state.shiftCode);
    }));
    return Array.from(found).sort();
  }, [filteredRoster, days, getCellState]);

  /**
   * Active shifts whose `color` is blank, so the grid is painting a derived stand-in for them.
   *
   * Read from the configuration rather than from the rendered cells: a shift can be configured
   * without a colour and simply not be assigned to anyone this month, and HR still wants to know
   * before they wonder why next month looks wrong.
   */
  const shiftsMissingColor = useMemo(
    () => activeShifts
      .filter(s => !(s.color || '').trim())
      .map(s => s.shiftCode)
      .sort(),
    [activeShifts]
  );

  /**
   * Whether this viewer may change this employee's roster.
   *
   * Per row, because every role now sees every employee: a Manager gets the whole table but may
   * only edit their own team — themselves and their reports. Previously the roster was filtered
   * instead, so a single page-level `canManage` was enough — with the filter gone, a page-level
   * flag would have made every row look editable and pushed the refusal down to the backend,
   * which is exactly the "Authorization denied: Employee does not report to you" toast this
   * replaces.
   *
   * `emp.employeeEmail` is passed alongside `emp.managerEmail` so canEditEmployeeRoster can
   * recognise the manager's own row; a manager's managerEmail names their manager, so the
   * reporting-line check alone locks them out of the row they most obviously own.
   */
  const canEditRow = useCallback(
    (emp: EmployeeShiftRow): boolean =>
      !!user?.role &&
      canEditEmployeeRoster(user.role, emp.employeeEmail, emp.managerEmail, user.email, sameIdentity),
    [user?.role, user?.email]
  );

  /** The subset of the visible roster this viewer may actually change. */
  const editableRoster = useMemo(
    () => filteredRoster.filter(r => canEditRow(r)),
    [filteredRoster, canEditRow]
  );

  // ── Cell interaction & Inline Auto-Save ──────────────────────
  const handleCellClick = (empId: string, date: string, el: HTMLElement) => {
    if (!canManage) return;
    // Re-checked here as well as on the cell, so a stale render cannot open an editor the
    // viewer is not allowed to use.
    const target = roster.find(r => r.employeeId === empId);
    if (target && !canEditRow(target)) return;
    setActiveCell(prev => (prev?.empId === empId && prev?.date === date ? null : { empId, date, el }));
  };

  const handleShiftSelect = async (empId: string, date: string, code: string) => {
    const cellKey = `${empId}:${date}`;
    setActiveCell(null);
    setSavingCellKey(cellKey);

    // Optimistic UI Update
    setRoster(prevRoster =>
      prevRoster.map(r => {
        if (r.employeeId !== empId) return r;
        return { ...r, shifts: { ...r.shifts, [date]: code } };
      })
    );

    try {
      await ShiftRosterService.saveAssignment({ employeeId: empId, date, shiftCode: code });
      showToast(`Shift updated to ${code}`, 'success');
    } catch (err: any) {
      console.error('[ShiftRoster] Save error:', err);
      showToast(err?.message || 'Failed to update shift', 'error');
      await loadRoster(); // Rollback on error
    } finally {
      setSavingCellKey(null);
    }
  };

  const handleClearOverride = async (empId: string, date: string) => {
    const cellKey = `${empId}:${date}`;
    setActiveCell(null);
    setSavingCellKey(cellKey);

    // Optimistic UI Update
    setRoster(prevRoster =>
      prevRoster.map(r => {
        if (r.employeeId !== empId) return r;
        const updatedShifts = { ...r.shifts };
        delete updatedShifts[date];
        return { ...r, shifts: updatedShifts };
      })
    );

    try {
      await ShiftRosterService.deleteShiftAssignment(empId, date);
      showToast('Shift override cleared — reverted to Primary/Default Shift', 'success');
    } catch (err: any) {
      console.error('[ShiftRoster] Clear override error:', err);
      showToast(err?.message || 'Failed to clear override', 'error');
      await loadRoster(); // Rollback on error
    } finally {
      setSavingCellKey(null);
    }
  };

  // ── Range Override (apply a shift to a date range directly from the grid) ──
  const handleRangeApply = async () => {
    if (!rangeShift || !rangeFrom || !rangeTo) { showToast('Choose employee(s), dates and a shift', 'error'); return; }
    if (rangeTo < rangeFrom) { showToast('End date must be on or after start date', 'error'); return; }
    /*
     * "All visible" means all visible *and editable*. Now that a Manager sees the whole company,
     * an unfiltered ALL would post an assignment for every employee and collect a wall of
     * per-row authorization failures from the backend.
     */
    const targetEmps = rangeEmp === 'ALL'
      ? editableRoster.map(r => r.employeeId)
      : [rangeEmp];
    if (targetEmps.length === 0) { showToast('No employees you can edit are in view', 'error'); return; }
    setRangeBusy(true);
    try {
      const inputs: CreateShiftAssignmentInput[] = [];
      for (const empId of targetEmps) {
        const cur = new Date(rangeFrom);
        const end = new Date(rangeTo);
        while (cur <= end) {
          inputs.push({ employeeId: empId, date: cur.toISOString().split('T')[0], shiftCode: rangeShift, changeReason: 'Grid range override' });
          cur.setDate(cur.getDate() + 1);
        }
      }
      const res = await ShiftRosterService.batchSaveAssignments(inputs);
      if (res.failureCount > 0) showToast(`${res.successCount} applied, ${res.failureCount} failed`, 'error');
      else showToast(`${res.successCount} shift day(s) updated`, 'success');
      setRangeOpen(false); setRangeFrom(''); setRangeTo(''); setRangeShift('');
      await loadRoster();
    } catch (e: any) {
      console.error('[ShiftRoster] range override failed:', e);
      showToast(e?.message || 'Range override failed', 'error');
    } finally {
      setRangeBusy(false);
    }
  };

  // ── Export CSV ────────────────────────────────────────────────
  const handleExport = () => {
    // 'Team' added rather than replacing 'Job Title': the export is reconciled against other HR
    // systems, so removing a column that was already there would break whatever consumes it.
    const headers = ['Employee', 'Employee ID', 'Email', 'Team', 'Job Title', 'Primary Shift', 'REG Days', 'OFF Days',
      ...days.map(d => `${String(d.day).padStart(2,'0')} ${d.weekday}`)];
    const rows = filteredRoster.map(emp => [
      `"${emp.employeeName}"`,
      // The HR employee number, not the Cognito account id the column used to carry — an
      // exported roster is read by people, and a column of azuread_/UUID strings is unusable
      // for reconciling against any other HR system. Email added for the same reason.
      `"${emp.employeeCode || ''}"`,
      `"${emp.employeeEmail || ''}"`,
      `"${emp.team || ''}"`,
      `"${emp.department}"`,
      `"${emp.shift || ''}"`,
      getRegCount(emp),
      getOffCount(emp),
      ...days.map(d => {
        const state = getCellState(emp, d);
        return `"${state.shiftCode}"`;
      }),
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `shift-roster-${MONTHS[month]}-${year}.csv`;
    a.click();
  };

  // ── Month navigation ──────────────────────────────────────────
  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="sr-details">

      {/*
        Stated rather than left to be discovered by clicking. Employees can now open this
        screen, and a grid whose cells simply do nothing on click reads as broken.
      */}
      {!canManage && (
        <div className="sr-readonly-notice" role="status">
          <span aria-hidden="true">👁</span>
          <span>
            <strong>Read-only.</strong> Your manager and HR set these shifts.
          </span>
        </div>
      )}
      {/*
        Says up front which rows a manager can act on. Without it, the whole company appearing in
        an editable-looking grid invites the click that used to fail with an authorization toast.
      */}
      {canManage && editableRoster.length !== filteredRoster.length && (
        <div className="sr-readonly-notice" role="status">
          <span aria-hidden="true">👁</span>
          <span>
            <strong>You can edit {editableRoster.length} of {filteredRoster.length} rows.</strong>{' '}
            The whole roster is visible; only your own row and your direct reports can be changed.
          </span>
        </div>
      )}

      {/* ── Toolbar ── */}
      <div className="sr-filters">
        <div className="sr-filter-left">
          <div className="sr-month-nav">
            <button className="sr-nav-btn" onClick={prevMonth} title="Previous Month">‹</button>
            <select value={month} onChange={e => setMonth(Number(e.target.value))} className="sr-select">
              {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
            <select value={year} onChange={e => setYear(Number(e.target.value))} className="sr-select">
              {/* The stored year can sit outside the offered range if month navigation
                  walked past it — keep it listed so the select never shows a blank value. */}
              {Array.from(new Set([...selectableYears, year])).sort((a, b) => a - b)
                .map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button className="sr-nav-btn" onClick={nextMonth} title="Next Month">›</button>
          </div>
          <input
            type="text"
            placeholder="🔍 Search employee..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="sr-search"
          />
          <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} className="sr-select">
            <option value="">All Departments</option>
            {teamOptions.map(d => <option key={d} value={d}>{d}</option>)}
            {unassignedCount > 0 && (
              <option value={UNASSIGNED_TEAM}>No team set ({unassignedCount})</option>
            )}
          </select>
        </div>

        <div className="sr-filter-right">
          {canManage && <button className="sr-btn-outline" onClick={() => setRangeOpen(true)}>📆 Range Override</button>}
          <button className="sr-btn-outline" onClick={handleExport}>📥 Export CSV</button>
        </div>
      </div>

      {/* ── Range Override panel ── */}
      <RightSidePanel
        isOpen={rangeOpen}
        title="Range Shift Override"
        onClose={() => setRangeOpen(false)}
      >
        <div className="sr-form">
          <p className="sr-bulk-desc">
            Apply a shift to every day in a date range. Only employees whose roster you may edit
            are listed{canManage && editableRoster.length !== filteredRoster.length
              ? ` — ${editableRoster.length} of the ${filteredRoster.length} in view`
              : ''}.
          </p>
          <label>Employee
            <select className="sr-select" value={rangeEmp} onChange={e => setRangeEmp(e.target.value)}>
              <option value="ALL">All employees you can edit ({editableRoster.length})</option>
              {editableRoster.map(r => (
                <option key={r.employeeId} value={r.employeeId}>{r.employeeName}</option>
              ))}
            </select>
          </label>
          <div className="sr-form-pair">
            <label>From Date <input type="date" value={rangeFrom} onChange={e => setRangeFrom(e.target.value)} /></label>
            <label>To Date <input type="date" value={rangeTo} min={rangeFrom} onChange={e => setRangeTo(e.target.value)} /></label>
          </div>
          <label>Shift
            <select className="sr-select" value={rangeShift} onChange={e => setRangeShift(e.target.value)}>
              <option value="">Select shift…</option>
              {activeShifts.map(s => <option key={s.shiftCode} value={s.shiftCode}>{nightPrefix(s)}{s.shiftCode} — {s.shiftName}</option>)}
            </select>
          </label>
          <div className="sr-confirm-actions">
            <button type="button" className="sr-btn-cancel" onClick={() => setRangeOpen(false)} disabled={rangeBusy}>Cancel</button>
            <button type="button" className="sr-btn-save" onClick={handleRangeApply} disabled={rangeBusy || !rangeShift || !rangeFrom || !rangeTo}>
              {rangeBusy ? 'Applying…' : 'Apply Range'}
            </button>
          </div>
        </div>
      </RightSidePanel>

      {/* ── Roster Grid ── */}
      {loading ? (
        <SkeletonRosterTable />
      ) : loadError ? (
        <div className="sr-error-card">
          <div className="sr-error-icon">⚠️</div>
          <h3>Unable to Load User Management Data</h3>
          <p>{loadError}. Could not reach User Management service.</p>
          <button className="sr-btn-retry" onClick={loadRoster}>
            🔄 Retry Connection
          </button>
        </div>
      ) : filteredRoster.length === 0 ? (
        <div className="sr-empty-card">
          <h3>No employees found</h3>
          <p>Try adjusting your search or department filter.</p>
        </div>
      ) : (
        <div className="sr-table-wrapper">
          {/*
            An assigned code that matches no configured shift, named up front.

            This is the state behind "I changed the colour and the cells did not move": a cell whose
            stored value resolves to no active shift gets no fill and falls back to the plain themed
            background, so the legend shows the new colour while the grid looks untouched. The
            information already existed — a small chip in the cell, a warning in its tooltip, a line
            in the legend — but all three are easy to miss on a 31-column grid, which is why it read
            as a colour bug rather than as a data problem. Said here instead, where it cannot be
            walked past, with the codes spelled out so HR knows what to reassign.
          */}
          {staleCodesInUse.length > 0 && (
            <div className="sr-stale-banner" role="status">
              <span className="sr-stale-banner-icon" aria-hidden="true">⚠</span>
              <span>
                <strong>{staleCodesInUse.join(', ')}</strong>{' '}
                {staleCodesInUse.length === 1 ? 'is no longer an active shift.' : 'are no longer active shifts.'}{' '}
                Cells using {staleCodesInUse.length === 1 ? 'it' : 'them'} have no colour and no
                hours behind them. Reassign those days, or add the shift back in Shift Configuration.
              </span>
            </div>
          )}
          {/*
            A configured shift with a blank colour. Reported separately from a stale code because it
            is a different problem with a different fix — the shift is real, it just has nothing to
            paint with — and because this is the case that had no signal at all: the cell was left
            unpainted, which is indistinguishable from an empty cell, and the staleness check did not
            flag it because the shift does exist. The grid now paints a derived stand-in so the cell
            is at least visible, and says here that it is not the colour anyone chose.
          */}
          {shiftsMissingColor.length > 0 && (
            <div className="sr-stale-banner" role="status">
              <span className="sr-stale-banner-icon" aria-hidden="true">⚠</span>
              <span>
                <strong>{shiftsMissingColor.join(', ')}</strong>{' '}
                {shiftsMissingColor.length === 1 ? 'has no colour set.' : 'have no colour set.'}{' '}
                The grid is showing a stand-in. Pick a colour in Shift Configuration and it will
                use that instead.
              </span>
            </div>
          )}
          <table className="sr-table">
            <thead>
              <tr>
                <th className="sr-th-sticky sr-th-emp">Employee</th>
                {/* Team, not the Job Title this column used to show — it is what the filter
                    beside it matches, and a column filtered on something invisible is what
                    made the filter look broken. */}
                <th className="sr-th-sticky sr-th-dept" title="Team / Department, from Organisation Setup">Team</th>
                <th className="sr-th-sticky sr-th-reg" title="Regular Working Days">REG</th>
                {days.map(d => (
                  <th key={d.date} className={`sr-th-day ${d.weekday === 'Sun' || d.weekday === 'Sat' ? 'weekend' : ''}`}>
                    <span className="sr-day-num">{String(d.day).padStart(2,'0')}</span>
                    <span className="sr-day-name">{d.weekday}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRoster.map(emp => (
                <tr key={emp.employeeId}>
                  {/* Employee column */}
                  <td className="sr-td-sticky sr-td-emp">
                    <div className="sr-emp-cell">
                      <div className="sr-emp-info">
                        {/*
                          Name only. The employee number used to print on a second line under it,
                          which cost every row a line of height on a sheet whose value is how many
                          rows and columns fit at once — and the number is not what anyone reads a
                          roster by. It still orders the rows (see filteredRoster), still matches
                          the search box, still appears in the CSV export, and is on this tooltip
                          for when it is actually wanted.
                        */}
                        <div
                          className="sr-emp-name"
                          title={`${emp.employeeName}\nEmployee ID: ${emp.employeeCode || 'not set'}\nEmail: ${emp.employeeEmail}`}
                        >
                          {emp.employeeName}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Team column. The Job Title stays available in the tooltip and in the CSV
                      export, so nothing that was visible before has been lost. */}
                  <td className="sr-td-sticky sr-td-dept">
                    <div
                      className={`sr-dept-name ${emp.team ? '' : 'sr-dept-name--unset'}`}
                      title={`Team: ${emp.team || 'not set'}\nJob title: ${emp.department || 'not set'}`}
                    >
                      {emp.team || '—'}
                    </div>
                  </td>

                  {/* REG count */}
                  <td className="sr-td-sticky sr-td-reg">{getRegCount(emp)}</td>

                  {/* Date cells */}
                  {days.map(d => {
                    const cellKey = `${emp.employeeId}:${d.date}`;
                    const rowEditable = canEditRow(emp);
                    const cellState = getCellState(emp, d);
                    const isActive = activeCell?.empId === emp.employeeId && activeCell?.date === d.date;
                    const isSaving = savingCellKey === cellKey;
                    const isWeekend = d.weekday === 'Sun' || d.weekday === 'Sat';

                    // ── PRIORITY 1: Approved Leave (non-editable) ──
                    if (cellState.isLeave) {
                      const leave = leaveChipDisplay(cellState.leaveRequest?.type || cellState.leaveCode || '', isDarkMode);
                      return (
                        <td
                          key={d.date}
                          className={`sr-cell sr-cell-leave ${isWeekend ? 'weekend' : ''}`}
                          style={{ cursor: 'not-allowed' }}
                          title={`Approved Leave — ${leave.label} (${leave.code})${cellState.leaveRequest?.reason ? ': ' + cellState.leaveRequest.reason : ''}`}
                        >
                          {/*
                            A saturated chip on the cell's own background, while a shift fills the
                            whole cell. That structural difference is what keeps leave and shift
                            apart no matter how either palette is configured — colour alone was
                            not enough, and had already collided (Work From Home against General
                            Shift measured 59 apart, On Duty against Early Morning 66).
                          */}
                          <div className="sr-cell-content">
                            <span
                              className="sr-cell-code leave-chip"
                              style={{
                                backgroundColor: leave.background,
                                color: leave.text,
                                border: `1px solid ${leave.border}`,
                                fontWeight: 700,
                              }}
                            >
                              {leave.code}
                            </span>
                          </div>
                        </td>
                      );
                    }

                    /*
                     * Whole-cell fill from the configured ShiftMaster.
                     *
                     * The colour used to sit on a small chip inside the cell, so a month read as
                     * a grid of grey boxes with dots in them. It also bypassed configuration for
                     * OFF: "Weekly Off" is a real configured shift (#e2e8f0), but the cell was
                     * painted by a hardcoded `off-chip` class, so editing its colour in Shift
                     * Configuration changed nothing.
                     */
                    const shiftDisplay = shiftCellDisplay(cellState.shiftCode, shifts, isDarkMode);
                    /*
                     * A public holiday paints from the Public Holiday seed, not from the OFF
                     * shift it borrows its code from — and not from a CSS class either. The
                     * class `.sr-cell-holiday { background:#fffbe6 !important }` used to win
                     * over the inline fill, so the legend swatch and the cell it described were
                     * two unrelated colours. Both now come from leaveSeedColor('PH').
                     */
                    const holiday = cellState.isHoliday ? holidayTone(isDarkMode) : undefined;
                    const shiftColor = holiday?.background ?? shiftDisplay.background;
                    const cellTextColor = holiday?.text ?? shiftDisplay.text;

                    let cellTitle = `${emp.employeeName} — ${d.date}: ${cellState.shiftCode}`;
                    if (cellState.isHoliday) {
                      cellTitle += ` (${cellState.holidayName} - Holiday)`;
                    } else if (cellState.isOverride) {
                      cellTitle += ` (Manual Override)`;
                    } else if (cellState.isWeekend) {
                      cellTitle += ` (Weekly Off)`;
                    } else {
                      cellTitle += ` (Primary Shift)`;
                    }
                    if (cellState.isStale) {
                      cellTitle += `\n⚠ "${cellState.shiftCode}" is not an active shift in Shift Configuration —`
                        + ` it has no hours behind it. Reassign this employee.`;
                    }
                    // Keeps a withdrawn WFH/On Duty record visible without letting it claim the
                    // cell — the record is historical data, not a reason to hide the shift.
                    if (cellState.withdrawnLeaveNote) {
                      cellTitle += `\nℹ ${cellState.withdrawnLeaveNote}`;
                    }

                    return (
                      <React.Fragment key={d.date}>
                        <td
                          className={`sr-cell ${shiftColor ? 'sr-cell-filled' : ''} ${cellState.differsFromDefault ? 'modified' : ''} ${isActive ? 'active' : ''} ${isWeekend ? 'weekend' : ''} ${rowEditable ? '' : 'sr-cell-readonly'}`}
                          onClick={(e) => handleCellClick(emp.employeeId, d.date, e.currentTarget)}
                          title={
                            rowEditable
                              ? cellTitle
                              : `${cellTitle}\n(read-only${canManage ? ' — not you or one of your direct reports' : ''})`
                          }
                          /*
                           * Custom properties, not `backgroundColor` / `color` directly.
                           *
                           * App.css carries a global `[data-theme="dark"] td { background:
                           * transparent !important; color: #c0c0d4 !important }`, plus
                           * `tr:nth-child(even) td` and `tr:hover td` in the same style. An
                           * `!important` declaration in a stylesheet outranks an inline style, so
                           * every fill this grid set was computed away to transparent — the cell
                           * kept its inline value and the browser painted the table background.
                           * That is why the legend badges were correct while the cells were not:
                           * a badge is a <span>, and so is a leave chip, so neither is a <td>.
                           *
                           * A variable cannot be overridden by those rules, because they say
                           * nothing about it. The stylesheet rule that consumes it (see
                           * `.sr-cell-filled` in ShiftRosterPage.css) is specific enough to win,
                           * which keeps the fix inside this feature instead of editing a global
                           * rule that every other table in the app depends on.
                           */
                          style={shiftColor ? {
                            ['--sr-cell-fill' as string]: shiftColor,
                            ['--sr-cell-ink' as string]: cellTextColor,
                          } as React.CSSProperties : undefined}
                        >
                          <div className="sr-cell-content">
                            {/*
                              The default shift's code is not printed.
                              A roster where almost every cell reads GEN spends all its ink on
                              the unremarkable case, and the cells that actually need attention
                              — a night shift, a leave, a retired code — have to compete with it.
                              Any other shift keeps both its colour and its code, so what is
                              left visible is exactly what differs from the norm.

                              The chip itself still renders, coloured, so an assigned default
                              day stays distinguishable from one with no shift at all, and the
                              cell's title attribute always carries the code in full.
                            */}
                            {/*
                              Text only now — the fill moved to the cell itself, so a chip
                              background here would draw a box inside an identically coloured
                              box. Stale keeps its own chip precisely because it has no cell
                              fill to sit on.
                            */}
                            <span
                              className={`sr-cell-code ${cellState.isStale ? 'stale-chip' : ''}`}
                              style={shiftColor ? { color: cellTextColor } : undefined}
                            >
                              {/*
                                Stale codes keep their label whatever they are. Suppression
                                assumes the colour speaks for the code, which stops being true
                                the moment the shift is deleted or deactivated — a retired GEN
                                or OFF drawn as a silent coloured cell would hide exactly the
                                problem the stale chip exists to report.
                              */}
                              {isSaving
                                ? '⏳'
                                : !cellState.isStale && isUnlabelledShiftCode(cellState.shiftCode)
                                  ? ''
                                  : cellState.shiftCode}
                            </span>
                            {cellState.isHoliday && (
                              // Inherits the holiday tone's text colour rather than the amber it
                              // used to hardcode, which no longer matched the cell behind it.
                              <span
                                className="sr-holiday-badge"
                                title={cellState.holidayName}
                                style={{ color: cellTextColor }}
                              >
                                🎉 {cellState.holidayName}
                              </span>
                            )}
                          </div>
                        </td>
                        {isActive && activeCell && (
                          <ShiftCellDropdownPortal
                            anchorEl={activeCell.el}
                            activeShifts={activeShifts}
                            currentShiftCode={cellState.shiftCode}
                            isOverride={cellState.isOverride}
                            onSelectShift={(code) => handleShiftSelect(emp.employeeId, d.date, code)}
                            onClearOverride={() => handleClearOverride(emp.employeeId, d.date)}
                            onClose={() => setActiveCell(null)}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Dual Legend ── */}
      <div className="sr-legend-bar">
        <div className="sr-legend-group">
          <span className="sr-legend-title">Shift Types:</span>
          {activeShifts.map(s => {
            /*
             * The times come from the shift itself, not a hardcoded table.
             *
             * There used to be a per-code override list here, and it was a second source of
             * truth that had already gone wrong: it labelled EMS as "14:00 – 23:00" from when
             * EMS meant Evening Shift, so the moment EMS became the Early Morning Shift
             * this legend would have confidently displayed the wrong hours for it — while the
             * roster grid beside it used the real ones. Editing a shift's hours in Shift
             * Configuration also never reached this label.
             */
            const timingLabel = s.startTime && s.endTime ? `${s.startTime} – ${s.endTime}` : '';
            /*
             * The swatch is the tone the grid actually paints for this shift, produced by the
             * same shiftCellDisplay call the cells use. Painting s.color raw here meant the
             * legend showed the configured seed while the cell showed its toned version —
             * two different colours for one type, which is the mismatch being fixed.
             */
            const swatch = shiftCellDisplay(s.shiftCode, activeShifts, isDarkMode);

            return (
              <div key={s.shiftCode} className="sr-legend-item">
                <span
                  className="sr-legend-badge"
                  style={{
                    backgroundColor: swatch.background,
                    color: swatch.text,
                    border: `1px solid ${swatch.border || 'transparent'}`,
                  }}
                >
                  {s.shiftCode}
                </span>
                <span className="sr-legend-desc">
                  {isNightShift(s) && <NightIcon />}
                  {s.shiftName}{timingLabel ? ` (${timingLabel})` : ''}
                </span>
              </div>
            );
          })}

          {staleCodesInUse.length > 0 && (
            <div className="sr-legend-item">
              <span className="sr-legend-badge sr-legend-badge--stale">
                {staleCodesInUse.join(', ')}
              </span>
              <span className="sr-legend-desc">
                Not configured — assigned to an employee but no longer an active shift
              </span>
            </div>
          )}
        </div>

        {/*
          Leave swatches are produced by the same leaveChipDisplay the cells use, so the legend
          cannot claim a colour the grid does not draw. It previously read from its own
          hardcoded LEAVE_LEGEND table, which is how Casual, Sick and Earned all came to show
          the same amber here while the Leave module gave them green, red and blue.
        */}
        <div className="sr-legend-group">
          <span className="sr-legend-title">Special & Leave Types:</span>
          {LEAVE_LEGEND_TYPES.map(name => {
            const chip = leaveChipDisplay(name, isDarkMode);
            /*
             * Public Holiday is the one leave type the roster paints as a whole-cell fill rather
             * than a chip, so its swatch has to use the fill tone too. Showing the chip tone made
             * the legend a visibly different lilac (#c572cd) from the cell it described (#e3bbe7)
             * — the same legend-versus-cell mismatch this work set out to remove, reintroduced by
             * rendering the right seed at the wrong strength.
             */
            const swatch = chip.code === PUBLIC_HOLIDAY_CODE
              ? { ...chip, ...holidayTone(isDarkMode, 'fill') }
              : chip;
            return (
              <div key={chip.code} className="sr-legend-item">
                <span
                  className="sr-legend-badge"
                  style={{
                    backgroundColor: swatch.background,
                    color: swatch.text,
                    border: `1px solid ${swatch.border}`,
                  }}
                >
                  {chip.code}
                </span>
                <span className="sr-legend-desc">
                  {chip.label}
                  {chip.code === PUBLIC_HOLIDAY_CODE ? ' (fills the whole cell)' : ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};


// ════════════════════════════════════════════════════════════════
// TAB 2: SHIFT CONFIGURATION
// ════════════════════════════════════════════════════════════════

interface ShiftConfigTabProps {
  shifts: ShiftMaster[];
  loading: boolean;
  onReload: () => void;
  showToast: (msg: string, type: 'success' | 'error') => void;
}

type ShiftFormState = {
  shiftCode: string;
  shiftName: string;
  description: string;
  startTime: string;
  endTime: string;
  breakDuration: number;
  workingHours: number;
  weekendDays: number[];
  isNightShift: boolean;
  lateGraceMinutes: number;
  earlyExitMinutes: number;
  overtimeEligible: boolean;
  color: string;
};

/** A complete 6-digit hex. Shorthand is rejected so what is stored is always comparable. */
const isValidHex = (value: string): boolean => /^#[0-9a-f]{6}$/i.test((value || '').trim());

/**
 * Starting points, not the available range.
 *
 * Any colour can be chosen — these are one click for the common cases. Kept deliberately spread
 * across hue families so a shift picked from here is unlikely to collide with another.
 */
const SHIFT_COLOR_SHORTCUTS = [
  '#c7d2fe', '#99f6e4', '#64748b', '#e2e8f0',
  '#fed7aa', '#bbf7d0', '#fbcfe8', '#fef08a',
];

const emptyShiftForm = (): ShiftFormState => ({
  shiftCode: '', shiftName: '', description: '',
  startTime: '', endTime: '', breakDuration: 0, workingHours: 0,
  weekendDays: [0, 6], isNightShift: false,
  lateGraceMinutes: 0, earlyExitMinutes: 0, overtimeEligible: false,
  color: '#3b82f6',
});

// Net working hours = gross span (crossing midnight if end <= start) minus break.
const calcNetHours = (start: string, end: string, breakMinutes: number): number => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  if ([sh, sm, eh, em].some(n => Number.isNaN(n))) return 0;
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins <= 0) mins += 1440;
  mins -= breakMinutes || 0;
  return Math.max(0, Math.round((mins / 60) * 10) / 10);
};

type SortField = 'name' | 'code' | 'hours' | 'status';

const ShiftConfigTab: React.FC<ShiftConfigTabProps> = ({ shifts, loading, onReload, showToast }) => {
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [nightFilter, setNightFilter] = useState<'all' | 'night' | 'day'>('all');
  const [weekendFilter, setWeekendFilter] = useState<string>('all');
  const [sortField, setSortField]     = useState<SortField>('code');
  const [sortAsc, setSortAsc]         = useState(true);

  const [showForm, setShowForm]         = useState(false);
  const [formMode, setFormMode]         = useState<'create' | 'edit' | 'clone'>('create');
  const [editingShift, setEditingShift] = useState<ShiftMaster | null>(null);
  const [saving, setSaving]             = useState(false);
  const [form, setForm]                 = useState<ShiftFormState>(emptyShiftForm());
  const { isDarkMode } = useTheme();

  /*
   * The hex the field is showing, kept separately from form.color.
   *
   * Typing a hex passes through intermediate states that are not colours — "#", "#6", "#64" — and
   * writing those straight to form.color would blank the preview on every keystroke and could
   * save a half-typed value. The draft holds whatever is typed; form.color only advances when the
   * draft is a complete colour.
   */
  const [colorDraft, setColorDraft] = useState<string>(form.color);

  /*
   * Re-seed the draft when the drawer opens on a different shift (create / edit / clone).
   *
   * form.color is deliberately not a dependency: it changes on every keystroke through
   * applyColor, and reacting to it would overwrite the draft the user is mid-way through typing
   * with the value that draft just produced.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setColorDraft(form.color); }, [showForm, formMode, editingShift?.id]);

  const applyColor = useCallback((next: string) => {
    const value = next.startsWith('#') || next === '' ? next : `#${next}`;
    setColorDraft(value);
    if (isValidHex(value)) setForm(f => ({ ...f, color: value.toLowerCase() }));
  }, []);

  /** The tone the grid will paint for this colour — see the note at the preview. */
  const previewTone = useMemo(
    () => toneFrom(isValidHex(colorDraft) ? colorDraft : form.color, isDarkMode, 'fill'),
    [colorDraft, form.color, isDarkMode]
  );

  /**
   * An existing active shift whose painted fill is too close to this one, if any.
   *
   * Advisory only. A full month of cells is read by colour before it is read by code, and the
   * General/Night pair proved how easily two configured colours land close enough to be
   * indistinguishable once toned. Excludes the shift being edited, which is trivially itself.
   */
  const nearestShift = useMemo(
    () => (isValidHex(colorDraft)
      ? findConfusableShift(colorDraft, shifts, editingShift?.id)
      : undefined),
    [colorDraft, shifts, editingShift]
  );

  // The anchor element travels with the id: RowMenuPortal positions against the button,
  // and the menu no longer lives inside the row to find it from.
  const [openMenu, setOpenMenu] = useState<{ id: string; el: HTMLElement } | null>(null);
  const [confirm, setConfirm]       = useState<{ type: 'delete' | 'deactivate'; shift: ShiftMaster } | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [confirmBusy, setConfirmBusy]   = useState(false);
  /*
   * The "Add standard shifts" action is gone along with the built-in set behind it.
   *
   * It created six shifts defined in source, which decided an organisation's shift structure
   * in code and would re-add a code someone had deliberately deleted. Shifts are defined here,
   * with + Create Shift, so each one carries a real createdBy / updatedBy / version.
   */

  // ── Summary cards: real counts from the full loaded shift list ──
  const totalShifts = shifts.length;
  const activeCount = useMemo(() => shifts.filter(s => s.isActive).length, [shifts]);
  const inactiveCount = totalShifts - activeCount;
  const nightCount = useMemo(() => shifts.filter(isNightShift).length, [shifts]);

  const weekendPatternOptions = useMemo(() => {
    const map = new Map<string, string>();
    shifts.forEach(s => {
      const key = weekendPatternKey(s);
      if (!map.has(key)) map.set(key, weekendPatternLabel(s));
    });
    return Array.from(map.entries());
  }, [shifts]);

  // Search + weekend narrow the pool for BOTH facets; status and day/night are the two
  // independent facets that combine with each other.
  const searchWeekendFiltered = useMemo(() => {
    let r = shifts;
    if (weekendFilter !== 'all') r = r.filter(s => weekendPatternKey(s) === weekendFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter(s => s.shiftCode.toLowerCase().includes(q) || s.shiftName.toLowerCase().includes(q));
    }
    return r;
  }, [shifts, weekendFilter, search]);

  const matchesStatus = useCallback(
    (s: ShiftMaster) => statusFilter === 'all' || (statusFilter === 'active' ? s.isActive : !s.isActive),
    [statusFilter]
  );
  const matchesNight = useCallback(
    (s: ShiftMaster) => nightFilter === 'all' || (nightFilter === 'night' ? isNightShift(s) : !isNightShift(s)),
    [nightFilter]
  );

  /** Counts each facet shows what you'd get by picking it, with the OTHER facet still
   *  applied — so a badge never promises rows that a click won't actually produce. */
  const statusCounts = useMemo(() => {
    const pool = searchWeekendFiltered.filter(matchesNight);
    return { all: pool.length, active: pool.filter(s => s.isActive).length, inactive: pool.filter(s => !s.isActive).length };
  }, [searchWeekendFiltered, matchesNight]);

  const nightCounts = useMemo(() => {
    const pool = searchWeekendFiltered.filter(matchesStatus);
    return { all: pool.length, night: pool.filter(isNightShift).length, day: pool.filter(s => !isNightShift(s)).length };
  }, [searchWeekendFiltered, matchesStatus]);

  const filtersDirty = statusFilter !== 'all' || nightFilter !== 'all' || weekendFilter !== 'all' || search.trim() !== '';
  const clearFilters = useCallback(() => {
    setStatusFilter('all'); setNightFilter('all'); setWeekendFilter('all'); setSearch('');
  }, []);

  const filteredSorted = useMemo(() => {
    const r = searchWeekendFiltered.filter(s => matchesStatus(s) && matchesNight(s));
    const sorted = [...r].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') cmp = a.shiftName.localeCompare(b.shiftName);
      else if (sortField === 'code') cmp = a.shiftCode.localeCompare(b.shiftCode);
      else if (sortField === 'hours') cmp = (a.workingHours || 0) - (b.workingHours || 0);
      else if (sortField === 'status') cmp = Number(a.isActive) - Number(b.isActive);
      return sortAsc ? cmp : -cmp;
    });
    return sorted;
  }, [searchWeekendFiltered, matchesStatus, matchesNight, sortField, sortAsc]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortAsc(a => !a);
    else { setSortField(field); setSortAsc(true); }
  };
  const sortArrow = (field: SortField) => (sortField === field ? (sortAsc ? ' ▲' : ' ▼') : '');

  // ── Create / Edit / Clone ──
  const openCreate = () => {
    setFormMode('create');
    setEditingShift(null);
    setForm(emptyShiftForm());
    setShowForm(true);
  };

  const openEdit = (s: ShiftMaster) => {
    setFormMode('edit');
    setEditingShift(s);
    setForm({
      shiftCode: s.shiftCode, shiftName: s.shiftName, description: s.description || '',
      startTime: s.startTime, endTime: s.endTime, breakDuration: s.breakDuration || 0, workingHours: s.workingHours || 0,
      weekendDays: getWeekendDays(s), isNightShift: !!s.isNightShift,
      lateGraceMinutes: s.lateGraceMinutes || 0, earlyExitMinutes: s.earlyExitMinutes || 0,
      overtimeEligible: !!s.overtimeEligible, color: s.color,
    });
    setOpenMenu(null);
    setShowForm(true);
  };

  const openClone = (s: ShiftMaster) => {
    setFormMode('clone');
    setEditingShift(null);
    setForm({
      shiftCode: `${s.shiftCode}-COPY`.slice(0, 20), shiftName: `${s.shiftName} (Copy)`, description: s.description || '',
      startTime: s.startTime, endTime: s.endTime, breakDuration: s.breakDuration || 0, workingHours: s.workingHours || 0,
      weekendDays: getWeekendDays(s), isNightShift: !!s.isNightShift,
      lateGraceMinutes: s.lateGraceMinutes || 0, earlyExitMinutes: s.earlyExitMinutes || 0,
      overtimeEligible: !!s.overtimeEligible, color: s.color,
    });
    setOpenMenu(null);
    setShowForm(true);
  };

  const handleTimeChange = (field: 'startTime' | 'endTime', value: string) => {
    setForm(f => {
      const next = { ...f, [field]: value };
      next.workingHours = calcNetHours(next.startTime, next.endTime, next.breakDuration);
      if (formMode === 'create') next.isNightShift = deriveNightShiftClient(next.startTime, next.endTime);
      return next;
    });
  };

  const handleBreakChange = (breakDuration: number) => {
    setForm(f => ({ ...f, breakDuration, workingHours: calcNetHours(f.startTime, f.endTime, breakDuration) }));
  };

  const toggleWeekendDay = (day: number) => {
    setForm(f => ({
      ...f,
      weekendDays: f.weekendDays.includes(day)
        ? f.weekendDays.filter(d => d !== day)
        : [...f.weekendDays, day].sort((a, b) => a - b),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.shiftCode || !form.shiftName || !form.startTime || !form.endTime || !form.color) {
      showToast('Fill all required fields', 'error'); return;
    }
    const codeUpper = form.shiftCode.toUpperCase();
    const dup = shifts.find(s => s.shiftCode.toUpperCase() === codeUpper && s.id !== editingShift?.id);
    if (dup) { showToast(`Code "${form.shiftCode}" already exists`, 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        shiftCode: codeUpper,
        shiftName: form.shiftName,
        description: form.description || '',
        startTime: form.startTime,
        endTime: form.endTime,
        breakDuration: Number(form.breakDuration) || 0,
        workingHours: Number(form.workingHours) || 0,
        // The form has always collected these, but they were omitted from the payload —
        // so ticking "Night Shift" or picking weekend days silently did nothing on save.
        weekendDays: form.weekendDays,
        isNightShift: form.isNightShift,
        lateGraceMinutes: Number(form.lateGraceMinutes) || 0,
        earlyExitMinutes: Number(form.earlyExitMinutes) || 0,
        overtimeEligible: form.overtimeEligible,
        color: form.color || '#3b82f6',
      };
      if (formMode === 'edit' && editingShift) {
        await ShiftRosterService.updateShiftMaster({ id: editingShift.id, ...payload, expectedVersion: editingShift.version });
        showToast('Shift updated', 'success');
      } else {
        await ShiftRosterService.createShiftMaster({ ...payload, isActive: true });
        showToast(formMode === 'clone' ? 'Shift cloned successfully' : 'Shift created', 'success');
      }
      setShowForm(false);
      onReload();
    } catch (err: any) {
      showToast(err?.message || 'Failed to save shift', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Activate / Deactivate / Delete ──
  const quickActivate = async (s: ShiftMaster) => {
    setOpenMenu(null);
    try {
      await ShiftRosterService.updateShiftMaster({ id: s.id, isActive: true, expectedVersion: s.version });
      showToast(`"${s.shiftCode}" activated`, 'success');
      onReload();
    } catch (err: any) {
      showToast(err?.message || 'Failed to activate shift', 'error');
    }
  };

  const requestDeactivate = (s: ShiftMaster) => {
    setOpenMenu(null);
    setConfirmError(null);
    setConfirm({ type: 'deactivate', shift: s });
  };

  const requestDelete = (s: ShiftMaster) => {
    setOpenMenu(null);
    setConfirmError(null);
    setConfirm({ type: 'delete', shift: s });
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      if (confirm.type === 'deactivate') {
        await ShiftRosterService.updateShiftMaster({ id: confirm.shift.id, isActive: false, expectedVersion: confirm.shift.version });
        showToast(`"${confirm.shift.shiftCode}" deactivated`, 'success');
      } else {
        await ShiftRosterService.deleteShiftMaster(confirm.shift.id);
        showToast(`"${confirm.shift.shiftCode}" deleted`, 'success');
      }
      setConfirm(null);
      onReload();
    } catch (err: any) {
      setConfirmError(err?.message || `Failed to ${confirm.type} shift`);
    } finally {
      setConfirmBusy(false);
    }
  };

  const closeConfirm = () => { setConfirm(null); setConfirmError(null); };

  const dialog = (() => {
    if (!confirm) return null;
    if (confirmError && confirm.type === 'delete') {
      return {
        title: 'Cannot Delete Shift',
        message: <div className="sr-confirm-error">⚠ {confirmError}</div>,
        confirmLabel: 'Deactivate Instead',
        cancelLabel: 'Close',
        danger: false,
        onConfirm: () => { setConfirmError(null); setConfirm({ type: 'deactivate', shift: confirm.shift }); },
      };
    }
    if (confirmError) {
      return {
        title: 'Action Failed',
        message: <div className="sr-confirm-error">⚠ {confirmError}</div>,
        confirmLabel: 'Close',
        cancelLabel: null,
        danger: false,
        onConfirm: closeConfirm,
      };
    }
    if (confirm.type === 'delete') {
      return {
        title: 'Delete Shift',
        message: <>Permanently delete <strong>{confirm.shift.shiftCode} — {confirm.shift.shiftName}</strong>? This cannot be undone.</>,
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
        danger: true,
        onConfirm: handleConfirm,
      };
    }
    return {
      title: 'Deactivate Shift',
      message: <>Deactivate <strong>{confirm.shift.shiftCode} — {confirm.shift.shiftName}</strong>? It will no longer be selectable for new assignments. Historical roster records referencing it are unaffected.</>,
      confirmLabel: 'Deactivate',
      cancelLabel: 'Cancel',
      danger: false,
      onConfirm: handleConfirm,
    };
  })();

  return (
    <div className="sr-config">
      {/* Interactive Summary Stat Cards */}
      <div className="sr-stat-row">
        <button
          type="button"
          className={`sr-stat-card ${!filtersDirty ? 'active' : ''}`}
          onClick={clearFilters}
          title="Show all shifts"
        >
          <span className="sr-stat-value">{totalShifts}</span>
          <span className="sr-stat-label">Total Shifts</span>
        </button>

        <button
          type="button"
          className={`sr-stat-card sr-stat-card--good ${statusFilter === 'active' ? 'active' : ''}`}
          onClick={() => setStatusFilter(s => (s === 'active' ? 'all' : 'active'))}
          title="Filter active shifts"
        >
          <span className="sr-stat-value sr-stat-good">{activeCount}</span>
          <span className="sr-stat-label">Active</span>
        </button>

        <button
          type="button"
          className={`sr-stat-card sr-stat-card--muted ${statusFilter === 'inactive' ? 'active' : ''}`}
          onClick={() => setStatusFilter(s => (s === 'inactive' ? 'all' : 'inactive'))}
          title="Filter inactive shifts"
        >
          <span className="sr-stat-value sr-stat-muted">{inactiveCount}</span>
          <span className="sr-stat-label">Inactive</span>
        </button>

        <button
          type="button"
          className={`sr-stat-card sr-stat-card--night ${nightFilter === 'night' ? 'active' : ''}`}
          onClick={() => setNightFilter(n => (n === 'night' ? 'all' : 'night'))}
          title="Filter night shifts"
        >
          <span className="sr-stat-value sr-stat-night">{nightCount}</span>
          <span className="sr-stat-label">Night Shifts</span>
        </button>
      </div>

      {/* Toolbar: search + filters + create */}
      <div className="sr-config-toolbar">
        <input
          type="text"
          placeholder="🔍 Search by code or name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="sr-search"
        />
        <div className="sr-config-filters" role="group" aria-label="Filter by status">
          {(['all', 'active', 'inactive'] as const).map(f => (
            <button
              key={f}
              type="button"
              className={`sr-filter-btn ${statusFilter === f ? 'active' : ''}`}
              aria-pressed={statusFilter === f}
              onClick={() => setStatusFilter(f)}
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              <span className="sr-filter-count">{statusCounts[f]}</span>
            </button>
          ))}
        </div>
        <div className="sr-config-filters" role="group" aria-label="Filter by day or night">
          {(['all', 'night', 'day'] as const).map(f => (
            <button
              key={f}
              type="button"
              className={`sr-filter-btn ${nightFilter === f ? 'active' : ''}`}
              aria-pressed={nightFilter === f}
              onClick={() => setNightFilter(f)}
            >
              {f === 'all' ? 'All Shifts' : f === 'night' ? '🌙 Night' : '☀️ Day'}
              <span className="sr-filter-count">{nightCounts[f]}</span>
            </button>
          ))}
        </div>
        <select value={weekendFilter} onChange={e => setWeekendFilter(e.target.value)} className="sr-select">
          <option value="all">All Weekend Patterns</option>
          {weekendPatternOptions.map(([key, label]) => (
            <option key={key} value={key}>{label || 'None'} Off</option>
          ))}
        </select>
        {filtersDirty && (
          <button type="button" className="sr-btn-outline" onClick={clearFilters}>Clear filters</button>
        )}
        <span className="sr-toolbar-spacer" />
        <button type="button" className="sr-btn-save" onClick={openCreate}>+ Create Shift</button>
      </div>

      {/* Table / empty / skeleton */}
      {loading ? (
        <SkeletonShiftConfigTable />
      ) : shifts.length === 0 ? (
        <EmptyState
          icon="inbox"
          title="No shifts configured"
          subtitle="Shift definitions live here and nowhere else — until one exists the roster grid has no hours or colour to show, and employees cannot be assigned a shift. Use + Create Shift above to define your first one."
        />
      ) : filteredSorted.length === 0 ? (
        <EmptyState icon="search" title="No shifts match your filters" subtitle="Try a different search term or clear the filters above." />
      ) : (
        <div className="sr-shifts-table-wrapper">
          <table className="sr-shifts-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => toggleSort('code')}>Shift{sortArrow('code')}</th>
                <th>Timing</th>
                <th className="sortable" onClick={() => toggleSort('hours')}>Hours{sortArrow('hours')}</th>
                <th>Rules</th>
                <th>Weekend Off</th>
                <th className="sortable" onClick={() => toggleSort('status')}>Status{sortArrow('status')}</th>
                <th>Last Modified</th>
                <th className="sr-th-actions" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {filteredSorted.map(s => (
                <tr key={s.id} className={!s.isActive ? 'inactive' : ''}>
                  <td>
                    <div className="sr-shift-id-cell">
                      <span className="sr-config-dot" style={{ background: s.color }} />
                      <div>
                        <div className="sr-shift-code">
                          {s.shiftCode}
                          {isNightShift(s) && <NightIcon />}
                        </div>
                        <div className="sr-shift-name">{s.shiftName}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>{s.startTime} – {s.endTime}</div>
                    <div className="sr-muted">Break {s.breakDuration || 0}m</div>
                  </td>
                  <td>{s.workingHours ? `${s.workingHours}h` : '—'}</td>
                  <td>
                    <div className="sr-rule-badges">
                      {isNightShift(s) && <span className="sr-badge sr-badge-night">🌙 Night</span>}
                      {s.overtimeEligible && <span className="sr-badge sr-badge-ot">OT</span>}
                      {!isNightShift(s) && !s.overtimeEligible && <span className="sr-muted">—</span>}
                    </div>
                    <div className="sr-muted">Grace {s.lateGraceMinutes || 0}m · Early {s.earlyExitMinutes || 0}m</div>
                  </td>
                  <td>{weekendPatternLabel(s) || 'None'}</td>
                  <td>
                    <span className={`sr-config-badge ${s.isActive ? 'on' : 'off'}`}>{s.isActive ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td>
                    <div className="sr-muted">{formatDateShort(s.updatedAt)}</div>
                    {s.updatedBy && <div className="sr-muted-2" title={s.updatedBy}>{s.updatedBy}</div>}
                  </td>
                  <td className="sr-row-menu-wrap">
                    <button
                      className="sr-action-btn"
                      onClick={e => {
                        // Read synchronously: React clears currentTarget once the handler
                        // returns, and the updater below runs after that — reading it in
                        // there yields null, which then blew up in the portal's
                        // outside-click handler on anchorEl.contains().
                        const el = e.currentTarget;
                        setOpenMenu(prev => (prev?.id === s.id ? null : { id: s.id, el }));
                      }}
                      aria-label={`Actions for ${s.shiftCode}`}
                      aria-haspopup="menu"
                      aria-expanded={openMenu?.id === s.id}
                      title="Actions"
                    >
                      ⋮
                    </button>
                    {openMenu?.id === s.id && (
                      <RowMenuPortal anchorEl={openMenu.el} onClose={() => setOpenMenu(null)}>
                        <button className="sr-row-menu-item" onClick={() => openEdit(s)}>✎ Edit</button>
                        <button className="sr-row-menu-item" onClick={() => openClone(s)}>⧉ Clone</button>
                        {s.isActive ? (
                          <button className="sr-row-menu-item" onClick={() => requestDeactivate(s)}>⏸ Deactivate</button>
                        ) : (
                          <button className="sr-row-menu-item" onClick={() => quickActivate(s)}>▶ Activate</button>
                        )}
                        <button className="sr-row-menu-item danger" onClick={() => requestDelete(s)}>🗑 Delete</button>
                      </RowMenuPortal>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit / Clone form */}
      <RightSidePanel
        isOpen={showForm}
        wide
        title={formMode === 'edit' ? 'Edit Shift Configuration' : formMode === 'clone' ? 'Clone Shift Configuration' : 'Create New Shift'}
        onClose={() => setShowForm(false)}
      >
        <form className="sr-form" onSubmit={handleSubmit}>
          <div className="sr-form-section-title">General Information</div>
          <div className="sr-form-pair">
            <div className="sr-field">
              <div className="sr-field-label">
                <span>Shift Code</span>
                <span className="sr-req">*</span>
              </div>
              <input
                type="text"
                className="sr-field-input"
                value={form.shiftCode}
                onChange={e => setForm(f => ({ ...f, shiftCode: e.target.value.toUpperCase() }))}
                placeholder="e.g. MORNING"
                maxLength={20}
                required
                disabled={formMode === 'edit'}
              />
            </div>
            <div className="sr-field">
              <div className="sr-field-label">
                <span>Shift Name</span>
                <span className="sr-req">*</span>
              </div>
              <input
                type="text"
                className="sr-field-input"
                value={form.shiftName}
                onChange={e => setForm(f => ({ ...f, shiftName: e.target.value }))}
                placeholder="e.g. General Morning Shift"
                required
              />
            </div>
          </div>

          <div className="sr-field">
            <div className="sr-field-label">
              <span>Description</span>
            </div>
            <textarea
              className="sr-field-textarea"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Brief summary of shift timing, team applicability, or operational guidelines..."
              rows={2}
            />
          </div>

          <div className="sr-form-section-title">Timings & Work Hours</div>
          <div className="sr-form-pair">
            <div className="sr-field">
              <div className="sr-field-label">
                <span>Start Time</span>
                <span className="sr-req">*</span>
              </div>
              <input type="time" className="sr-field-input" value={form.startTime} onChange={e => handleTimeChange('startTime', e.target.value)} required />
            </div>
            <div className="sr-field">
              <div className="sr-field-label">
                <span>End Time</span>
                <span className="sr-req">*</span>
              </div>
              <input type="time" className="sr-field-input" value={form.endTime} onChange={e => handleTimeChange('endTime', e.target.value)} required />
            </div>
          </div>

          <div className="sr-form-pair">
            <div className="sr-field">
              <div className="sr-field-label">
                <span>Break Duration (min)</span>
              </div>
              <input type="number" className="sr-field-input" value={form.breakDuration} onChange={e => handleBreakChange(Number(e.target.value))} min={0} placeholder="e.g. 60" />
            </div>
            <div className="sr-field">
              <div className="sr-field-label">
                <span>Net Working Hours</span>
              </div>
              <input type="text" className="sr-field-input" value={`${form.workingHours} hrs`} disabled style={{ background: '#f8fafc', color: '#0d9488', fontWeight: 700 }} />
            </div>
          </div>

          <div className="sr-form-section-title">Weekly Off Pattern</div>
          <div className="sr-field">
            <div className="sr-field-label">
              <span>Select Off Days</span>
            </div>
            <div className="sr-weekday-row">
              {WEEKDAYS.map((wd, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`sr-weekday-chip ${form.weekendDays.includes(idx) ? 'sel' : ''}`}
                  onClick={() => toggleWeekendDay(idx)}
                  title={`Toggle ${wd} as weekly off`}
                >
                  {wd}
                </button>
              ))}
            </div>
            <small className="sr-form-hint" style={{ marginTop: '4px' }}>Days highlighted in teal count as weekly off days for employees assigned to this shift.</small>
          </div>

          <div className="sr-form-section-title">Rules & Classifications</div>
          <div className="sr-form-pair">
            <div className="sr-field">
              <div className="sr-field-label">
                <span>Late Grace (min)</span>
              </div>
              <input type="number" className="sr-field-input" value={form.lateGraceMinutes} onChange={e => setForm(f => ({ ...f, lateGraceMinutes: Number(e.target.value) }))} min={0} placeholder="e.g. 15" />
            </div>
            <div className="sr-field">
              <div className="sr-field-label">
                <span>Early Exit Grace (min)</span>
              </div>
              <input type="number" className="sr-field-input" value={form.earlyExitMinutes} onChange={e => setForm(f => ({ ...f, earlyExitMinutes: Number(e.target.value) }))} min={0} placeholder="e.g. 15" />
            </div>
          </div>

          <div className="sr-form-pair" style={{ marginTop: '6px' }}>
            <label className={`sr-checkbox-card ${form.isNightShift ? 'checked' : ''}`}>
              <input type="checkbox" checked={form.isNightShift} onChange={e => setForm(f => ({ ...f, isNightShift: e.target.checked }))} />
              <span>🌙 Night Shift</span>
            </label>
            <label className={`sr-checkbox-card ${form.overtimeEligible ? 'checked' : ''}`}>
              <input type="checkbox" checked={form.overtimeEligible} onChange={e => setForm(f => ({ ...f, overtimeEligible: e.target.checked }))} />
              <span>⚡ Overtime Eligible</span>
            </label>
          </div>

          <div className="sr-form-section-title">Badge Color</div>
          {/*
            Any colour, not a fixed set of eight.
            The native picker was already here but styled to look exactly like the presets beside
            it, so it read as a ninth swatch and nobody realised the full spectrum was available.
            It is now a labelled, full-width control with a typeable hex field, and the presets are
            demoted to what they actually are — shortcuts.
          */}
          <div className="sr-field">
            <div className="sr-field-label">
              <span>Accent Color</span>
              <span className="sr-req">*</span>
            </div>

            <div className="sr-color-choose">
              <label className="sr-color-well" title="Open the colour picker">
                <input
                  type="color"
                  value={colorDraft}
                  onChange={e => applyColor(e.target.value)}
                  aria-label="Pick any accent colour"
                />
                <span className="sr-color-well__text">
                  <strong>Pick any colour</strong>
                  <span>Full spectrum</span>
                </span>
              </label>

              <label className="sr-color-hex">
                <span className="sr-color-hex__label">Hex</span>
                <input
                  type="text"
                  value={colorDraft}
                  onChange={e => applyColor(e.target.value)}
                  spellCheck={false}
                  maxLength={7}
                  placeholder="#64748b"
                  aria-label="Accent colour hex value"
                  className={isValidHex(colorDraft) ? '' : 'invalid'}
                />
              </label>
            </div>

            {!isValidHex(colorDraft) && (
              <p className="sr-color-msg sr-color-msg--error">
                Enter a 6-digit hex colour, for example <code>#64748b</code>.
              </p>
            )}

            {/*
              Shows the toned fill the grid will actually paint, not the raw seed — the two are
              deliberately different, and a preview of the seed would promise a colour no cell
              ever shows.
            */}
            <div className="sr-color-preview" aria-live="polite">
              <span className="sr-color-preview__label">Roster cell</span>
              <span
                className="sr-color-preview__cell"
                style={{ backgroundColor: previewTone.background, color: previewTone.text }}
              >
                {form.shiftCode ? form.shiftCode.toUpperCase().slice(0, 4) : 'CODE'}
              </span>
              <span className="sr-color-preview__hint">
                {isDarkMode ? 'as it appears in dark mode' : 'as it appears in light mode'}
              </span>
            </div>

            {nearestShift && (
              <p className="sr-color-msg sr-color-msg--warn">
                Close to <strong>{nearestShift.shiftName}</strong> ({nearestShift.shiftCode}) — the
                two cells will be hard to tell apart. Still allowed, but a more distinct colour
                reads better on a full month.
              </p>
            )}

            <div className="sr-color-presets">
              <span className="sr-color-presets__label">Shortcuts</span>
              <div className="sr-color-row">
                {SHIFT_COLOR_SHORTCUTS.map(c => (
                  <button
                    key={c}
                    type="button"
                    className={`sr-swatch ${form.color.toLowerCase() === c ? 'sel' : ''}`}
                    style={{ background: c }}
                    onClick={() => applyColor(c)}
                    title={c}
                    aria-label={`Use ${c}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="sr-form-actions">
            <button type="button" className="sr-btn-drawer-cancel" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="sr-btn-drawer-submit" disabled={saving}>
              {saving ? 'Saving...' : formMode === 'edit' ? 'Update Shift' : formMode === 'clone' ? 'Create Cloned Shift' : 'Create Shift'}
            </button>
          </div>
        </form>
      </RightSidePanel>

      {dialog && (
        <ConfirmDialog
          open={!!confirm}
          title={dialog.title}
          message={dialog.message}
          confirmLabel={dialog.confirmLabel}
          cancelLabel={dialog.cancelLabel}
          danger={dialog.danger}
          busy={confirmBusy}
          onConfirm={dialog.onConfirm}
          onCancel={closeConfirm}
        />
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// TAB 3: BULK OVERRIDE
// ════════════════════════════════════════════════════════════════

interface BulkOverrideTabProps {
  shifts: ShiftMaster[];      // active shifts (selectable targets)
  allShifts: ShiftMaster[];   // all shifts incl. inactive (for weekend-pattern lookup)
  canManage: boolean;
  showToast: (msg: string, type: 'success' | 'error') => void;
}

interface BulkPreview {
  inputs: CreateShiftAssignmentInput[];
  employeesAffected: number;
  totalDays: number;
  skippedWeekend: number;
  skippedHoliday: number;
  skippedExisting: number;
  overwritten: number;
}

const BulkOverrideTab: React.FC<BulkOverrideTabProps> = ({ shifts, allShifts, canManage, showToast }) => {
  const { user } = useAuth();

  const [employees, setEmployees] = useState<BulkOverrideEmployee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [employeesError, setEmployeesError] = useState<string | null>(null);

  // Section 1 — selection
  const [search, setSearch]         = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [desigFilter, setDesigFilter] = useState('');
  const [shiftFilter, setShiftFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Section 2 — date range
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');

  // Section 3 — target shift
  const [shiftCode, setShiftCode] = useState('');

  // Section 4 — options
  const [reason, setReason] = useState('');
  const [applyToWeeklyOff, setApplyToWeeklyOff] = useState(false);
  const [applyToHolidays, setApplyToHolidays]   = useState(false);
  const [replaceExisting, setReplaceExisting]   = useState(true);

  // Sections 5-8 — preview / confirm / progress / results
  const [preview, setPreview]   = useState<BulkPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [result, setResult]     = useState<BatchShiftResult | null>(null);
  const [resultAt, setResultAt] = useState<string>('');

  /*
   * Loaded unfiltered and narrowed here with canEditEmployeeRoster, rather than asking
   * loadAllActiveEmployees for one manager's reports.
   *
   * Its `managerEmail` filter returns reports *only*, which left a manager unable to select
   * themselves for a bulk override even though the grid on Shift Details lets them edit their own
   * row. Filtering here instead means both tabs answer the question with the same function, so
   * they cannot drift apart again; loadAllActiveEmployees reads the shared user cache, so
   * dropping its argument costs no extra request. Employees never reach this tab (the access
   * check above), and for HR_Admin / Super_Admin the predicate passes everyone through.
   */
  useEffect(() => {
    setLoadingEmployees(true);
    setEmployeesError(null);
    loadAllActiveEmployees()
      .then(all => {
        /*
         * Leavers are dropped here too, on the same rule as the grid.
         *
         * loadAllActiveEmployees is "active" only in the Cognito sense — it drops disabled
         * accounts and knows nothing about dateOfExit — so somebody who left with their login
         * still enabled stayed selectable, and a bulk override would happily write shifts
         * across a month for a person who is not coming in.
         */
        const todayIso = CompOffService.todayIso();
        const users = all.filter(
          u => !hasLeftOrganisation(u, todayIso)
            && !!user?.role && canEditEmployeeRoster(user.role, u.email, u.managerEmail, user.email, sameIdentity)
        );
        setEmployees(users.map(u => ({
          id: u.id,
          name: u.fullName || u.name,
          email: u.email,
          department: u.department || 'General',
          designation: u.designation || '—',
          managerEmail: u.managerEmail,
          primaryShift: u.primaryShift || undefined,
        })));
      })
      .catch(err => {
        console.error('[ShiftRoster] loadAllActiveEmployees failed:', err);
        setEmployees([]);
        setEmployeesError('Unable to load employees');
      })
      .finally(() => setLoadingEmployees(false));
  }, [user?.email, user?.role]);

  /*
   * The same maintained Team / Department list User Management offers, not the distinct values
   * found on the loaded employees.
   *
   * Previously built from `e.department`, which is the free-text Job Title — so this dropdown listed
   * "Technical Lead", "mangaer" and "Emp" and could never agree with User Management. Reading
   * OrgConfigService.getTeams means an option exists here exactly when it exists there.
   */
  const [teamOptions, setTeamOptions] = useState<string[]>(() => OrgConfigService.getTeams());

  useEffect(() => {
    const readTeams = () => setTeamOptions(OrgConfigService.getTeams());
    OrgConfigService.refresh().finally(readTeams);
    window.addEventListener('org-config-updated', readTeams);
    return () => window.removeEventListener('org-config-updated', readTeams);
  }, []);

  /** Employees with no team set — surfaced rather than hidden, as on Shift Details. */
  const unassignedCount = useMemo(() => employees.filter(e => !e.team).length, [employees]);
  const designations = useMemo(() => Array.from(new Set(employees.map(e => e.designation).filter(Boolean))).sort(), [employees]);
  const usedShifts = useMemo(() => Array.from(new Set(employees.map(e => e.primaryShift).filter((s): s is string => !!s))).sort(), [employees]);

  const isFilterActive = !!search.trim() || !!deptFilter || !!desigFilter || !!shiftFilter;

  const visibleEmployees = useMemo(() => {
    if (!isFilterActive) return [];
    const term = search.trim().toLowerCase();
    return employees.filter(e => {
      /*
       * Null-safe on both fields. `name` is mapped as `fullName || name`, so an account with
       * neither attribute set arrives here as undefined — and `undefined.toLowerCase()` throws
       * inside this useMemo, which takes down the whole tab rather than skipping one row. One
       * nameless employee in the directory was enough to make the search box look dead.
       */
      if (term && !(e.name || '').toLowerCase().includes(term) && !(e.email || '').toLowerCase().includes(term)) return false;
      // Matched on `team`, the field backed by HR's maintained list — see teamOptions above.
      if (deptFilter) {
        if (deptFilter === UNASSIGNED_TEAM ? !!e.team : e.team !== deptFilter) return false;
      }
      if (desigFilter && e.designation !== desigFilter) return false;
      if (shiftFilter && e.primaryShift !== shiftFilter) return false;
      return true;
    });
  }, [employees, search, deptFilter, desigFilter, shiftFilter, isFilterActive]);

  const selectedEmployees = useMemo(() => {
    return employees.filter(e => selectedIds.has(e.id));
  }, [employees, selectedIds]);

  const toggleOne = (id: string) => {
    setSelectedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
    setPreview(null); setResult(null);
  };
  const selectAllVisible = () => { setSelectedIds(prev => { const next = new Set(prev); visibleEmployees.forEach(e => next.add(e.id)); return next; }); setPreview(null); setResult(null); };
  const clearSelection = () => { setSelectedIds(new Set()); setPreview(null); setResult(null); };

  const targetShift = useMemo(() => allShifts.find(s => s.shiftCode === shiftCode), [allShifts, shiftCode]);

  const datesInRange = (): string[] => {
    const out: string[] = [];
    if (!startDate || !endDate) return out;
    const cur = new Date(startDate); const end = new Date(endDate);
    while (cur <= end) { out.push(cur.toISOString().split('T')[0]); cur.setDate(cur.getDate() + 1); }
    return out;
  };

  const validate = (): string | null => {
    if (selectedIds.size === 0) return 'Select at least one employee';
    if (!startDate || !endDate) return 'Choose a start and end date';
    if (endDate < startDate) return 'End date must be on or after start date';
    if (!shiftCode) return 'Choose a target shift';
    if (!reason.trim()) return 'Override reason is required';
    return null;
  };

  // Section 5 — build the preview (fetches existing assignments to compute real record counts)
  const handlePreview = async () => {
    const err = validate();
    if (err) { showToast(err, 'error'); return; }
    setPreviewing(true);
    setResult(null);
    try {
      const ids = Array.from(selectedIds);
      const dates = datesInRange();
      const weekendDays = targetShift ? getWeekendDays(targetShift) : [0, 6];
      const inputs: CreateShiftAssignmentInput[] = [];
      let skippedWeekend = 0, skippedHoliday = 0, skippedExisting = 0, overwritten = 0;

      const existingByEmp = new Map<string, Set<string>>();
      await Promise.all(ids.map(async id => {
        const existing = await ShiftRosterService.getAssignmentsForEmployee(id, startDate, endDate);
        existingByEmp.set(id, new Set(existing.map(a => a.date)));
      }));

      for (const id of ids) {
        const existingDates = existingByEmp.get(id) || new Set<string>();
        for (const date of dates) {
          const dow = new Date(date).getDay();
          const isWeekend = weekendDays.includes(dow);
          const isHoliday = !!getHolidayForDate(date);
          if (isWeekend && !applyToWeeklyOff) { skippedWeekend++; continue; }
          if (isHoliday && !applyToHolidays) { skippedHoliday++; continue; }
          const hasExisting = existingDates.has(date);
          if (hasExisting && !replaceExisting) { skippedExisting++; continue; }
          if (hasExisting) overwritten++;
          inputs.push({ employeeId: id, date, shiftCode, changeReason: reason.trim() });
        }
      }

      setPreview({
        inputs,
        employeesAffected: new Set(inputs.map(i => i.employeeId)).size,
        totalDays: dates.length,
        skippedWeekend, skippedHoliday, skippedExisting, overwritten,
      });
    } catch (e) {
      console.error('[ShiftRoster] preview failed:', e);
      showToast('Failed to build preview', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirmApply = async () => {
    if (!preview || preview.inputs.length === 0) return;
    setConfirmOpen(false);
    setSaving(true);
    try {
      const res = await ShiftRosterService.batchSaveAssignments(preview.inputs);
      setResult(res);
      setResultAt(new Date().toLocaleString('en-IN'));
      if (res.failureCount > 0) showToast(`${res.successCount} applied, ${res.failureCount} failed`, 'error');
      else showToast(`${res.successCount} shift records updated`, 'success');
      setPreview(null);
    } catch {
      showToast('Bulk override failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const selectedCount = selectedIds.size;
  const empNameById = (id: string) => employees.find(e => e.id === id)?.name || id;

  if (!canManage) {
    return <EmptyState icon="inbox" title="Access Denied" subtitle="You do not have permission to perform bulk overrides." />;
  }
  if (employeesError) {
    return <EmptyState icon="inbox" title="Unable to load employees" subtitle="Could not reach User Management data. Please try again." />;
  }

  return (
    <div className="sr-bulk-v2">
      {/* Section 1 — Employee Selection */}
      <div className="sr-bulk-card">
        <div className="sr-bulk-card-head">
          <h3>1 · Select Employees</h3>
          <span className="sr-bulk-count">Selected: {selectedCount}</span>
        </div>

        {/* Selected Employees Chips Bar */}
        {selectedEmployees.length > 0 && (
          <div className="sr-selected-chips-bar">
            <strong className="sr-chips-title">Selected Employees ({selectedEmployees.length}):</strong>
            {selectedEmployees.map(e => (
              <span key={e.id} className="sr-emp-chip">
                {e.name}
                <button type="button" className="sr-chip-remove" onClick={() => toggleOne(e.id)} title="Remove employee" aria-label={`Remove ${e.name}`}>✕</button>
              </span>
            ))}
            <button type="button" className="sr-chips-clear" onClick={clearSelection}>Clear All</button>
          </div>
        )}

        <div className="sr-bulk-selectbar">
          <input className="sr-bulk-searchbox" type="text" placeholder="🔍 Search employees to add to selection…" value={search} onChange={e => setSearch(e.target.value)} />
          <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
            <option value="">All Departments</option>
            {teamOptions.map(d => <option key={d} value={d}>{d}</option>)}
            {unassignedCount > 0 && (
              <option value={UNASSIGNED_TEAM}>No team set ({unassignedCount})</option>
            )}
          </select>
          <select value={desigFilter} onChange={e => setDesigFilter(e.target.value)}>
            <option value="">All Designations</option>
            {designations.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={shiftFilter} onChange={e => setShiftFilter(e.target.value)}>
            <option value="">Any Current Shift</option>
            {usedShifts.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button type="button" className="sr-btn-outline" onClick={selectAllVisible} disabled={loadingEmployees || visibleEmployees.length === 0}>Select All ({visibleEmployees.length})</button>
          <button type="button" className="sr-btn-outline" onClick={clearSelection} disabled={selectedCount === 0}>Clear</button>
        </div>

        {loadingEmployees ? (
          <SkeletonRosterTable />
        ) : !isFilterActive ? (
          selectedEmployees.length > 0 ? (
            <div>
              <p style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: '8px' }}>Currently selected employees (type in search box above to add more):</p>
              <div className="sr-bulk-emplist">
                {selectedEmployees.map(e => (
                  <label key={e.id} className="sr-bulk-emprow selected">
                    <input type="checkbox" checked={selectedIds.has(e.id)} onChange={() => toggleOne(e.id)} />
                    <span className="sr-bulk-empname">{e.name}</span>
                    <span className="sr-bulk-empmeta">{e.department}{e.primaryShift ? ` · ${e.primaryShift}` : ''}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <p className="sr-bulk-desc" style={{ textAlign: 'center', padding: '28px 0', fontSize: '0.92rem' }}>
              🔍 Type an employee name or email in the search bar above to search and select employees.
            </p>
          )
        ) : visibleEmployees.length === 0 ? (
          <EmptyState icon="search" title="No employees match" subtitle="Adjust the search or filters." />
        ) : (
          <div className="sr-bulk-emplist">
            {visibleEmployees.map(e => (
              <label key={e.id} className={`sr-bulk-emprow ${selectedIds.has(e.id) ? 'selected' : ''}`}>
                <input type="checkbox" checked={selectedIds.has(e.id)} onChange={() => toggleOne(e.id)} />
                <span className="sr-bulk-empname">{e.name}</span>
                <span className="sr-bulk-empmeta">{e.department}{e.primaryShift ? ` · ${e.primaryShift}` : ''}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Section 2 — Date Range */}
      <div className="sr-bulk-card">
        <div className="sr-bulk-card-head"><h3>2 · Date Range</h3></div>
        <div className="sr-form-pair">
          <label>Start Date <input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPreview(null); }} /></label>
          <label>End Date <input type="date" value={endDate} min={startDate} onChange={e => { setEndDate(e.target.value); setPreview(null); }} /></label>
        </div>
        {startDate && endDate && endDate < startDate && <p className="sr-bulk-warn">End date must be on or after start date.</p>}
      </div>

      {/* Section 3 — Shift Selection */}
      <div className="sr-bulk-card">
        <div className="sr-bulk-card-head"><h3>3 · Target Shift</h3></div>
        <select value={shiftCode} onChange={e => { setShiftCode(e.target.value); setPreview(null); }} className="sr-select">
          <option value="">Select shift…</option>
          {shifts.map(s => <option key={s.shiftCode} value={s.shiftCode}>{nightPrefix(s)}{s.shiftCode} — {s.shiftName}</option>)}
        </select>
      </div>

      {/* Section 4 — Override Options */}
      <div className="sr-bulk-card">
        <div className="sr-bulk-card-head"><h3>4 · Override Options</h3></div>
        <label className="sr-bulk-field">
          Override Reason <span className="sr-req">*</span>
          <input type="text" value={reason} onChange={e => { setReason(e.target.value); setPreview(null); }} placeholder="e.g. Project go-live coverage" />
        </label>
        <div className="sr-bulk-checks">
          <label><input type="checkbox" checked={applyToWeeklyOff} onChange={e => { setApplyToWeeklyOff(e.target.checked); setPreview(null); }} /> Apply to Weekly Off days</label>
          <label><input type="checkbox" checked={applyToHolidays} onChange={e => { setApplyToHolidays(e.target.checked); setPreview(null); }} /> Apply to Holidays</label>
          <label><input type="checkbox" checked={replaceExisting} onChange={e => { setReplaceExisting(e.target.checked); setPreview(null); }} /> Replace existing overrides</label>
        </div>
      </div>

      {/* Section 5 — Preview */}
      <div className="sr-bulk-card">
        <div className="sr-bulk-card-head"><h3>5 · Preview</h3></div>
        {!preview ? (
          <p className="sr-bulk-desc">Build a preview to see exactly how many records will change before applying.</p>
        ) : (
          <div className="sr-bulk-preview">
            <div className="sr-bulk-stat"><span>{preview.employeesAffected}</span>Employees affected</div>
            <div className="sr-bulk-stat"><span>{preview.inputs.length}</span>Records to update</div>
            <div className="sr-bulk-stat"><span>{preview.overwritten}</span>Existing overwritten</div>
            <div className="sr-bulk-stat"><span>{preview.skippedWeekend}</span>Skipped (weekly off)</div>
            <div className="sr-bulk-stat"><span>{preview.skippedHoliday}</span>Skipped (holiday)</div>
            <div className="sr-bulk-stat"><span>{preview.skippedExisting}</span>Skipped (kept existing)</div>
            <div className="sr-bulk-preview-meta">
              Target: <strong>{shiftCode}</strong> · {startDate} → {endDate}
            </div>
          </div>
        )}
      </div>

      {/* Section 8 — Results */}
      {result && (
        <div className="sr-bulk-card sr-bulk-results">
          <div className="sr-bulk-card-head"><h3>Results</h3><span className="sr-bulk-count">{resultAt}</span></div>
          <p className={result.failureCount > 0 ? 'sr-bulk-warn' : 'sr-bulk-ok'}>
            ✓ {result.successCount} record(s) updated{result.failureCount > 0 ? ` · ✗ ${result.failureCount} failed` : ''}
          </p>
          {result.failedRecords && result.failedRecords.length > 0 && (
            <table className="sr-table" style={{ marginTop: 8 }}>
              <thead><tr><th>Employee</th><th>Date</th><th>Shift</th><th>Error</th></tr></thead>
              <tbody>
                {result.failedRecords.map((f, i) => (
                  <tr key={i}><td>{empNameById(f.employeeId)}</td><td>{f.date}</td><td>{f.shiftCode}</td><td>{f.error}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Sticky action bar (Sections 6/7 triggers) */}
      <div className="sr-bulk-actionbar">
        <span className="sr-bulk-actionbar-info">
          {selectedCount} selected{preview ? ` · ${preview.inputs.length} records ready` : ''}
        </span>
        <div className="sr-bulk-actionbar-btns">
          <button type="button" className="sr-btn-outline" onClick={handlePreview} disabled={previewing || saving}>
            {previewing ? 'Building…' : 'Preview'}
          </button>
          <button
            type="button"
            className="sr-btn-save"
            onClick={() => setConfirmOpen(true)}
            disabled={!preview || preview.inputs.length === 0 || saving}
          >
            {saving ? 'Applying…' : 'Apply Override'}
          </button>
        </div>
      </div>

      {/* Section 6 — Confirmation */}
      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Bulk Override"
        message={preview ? `You are about to update ${preview.inputs.length} shift record(s) across ${preview.employeesAffected} employee(s). Do you want to continue?` : ''}
        confirmLabel="Yes, Apply"
        busy={saving}
        onConfirm={handleConfirmApply}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default ShiftRosterPage;
